import React, {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  FaChartPie,
  FaTicketAlt,
  FaCog,
  FaDiscord,
  FaSignOutAlt,
  FaSync,
  FaExternalLinkAlt,
  FaPlus,
  FaTrash,
  FaArrowLeft,
  FaLock,
  FaUnlock,
  FaStar,
  FaSlidersH,
  FaClipboardList,
  FaTimes,
  FaChevronDown,
  FaSearch,
  FaCrown,
  FaLink,
  FaGlobe,
  FaCheckCircle,
  FaExclamationCircle,
  FaDoorOpen,
  FaUserShield,
  FaHistory,
  FaHeartbeat,
  FaUserCheck,
  FaCommentDots,
  FaFileAlt,
  FaPaperPlane,
  FaUsers,
  FaHeadset,
  FaChartLine,
} from 'react-icons/fa';
import Navbar from '../../components/Navbar/Navbar';
import discordService from '../../services/discordService';
import adminService from '../../services/adminService';
import { setSiteConfigCache, DEFAULT_SITE_CONFIG } from '../../services/siteConfigService';
import WelcomeSettingsPanel from '../../components/Admin/WelcomeSettingsPanel';
import MessageComposerPanel from '../../components/Admin/MessageComposerPanel';
import PermissionsPanel from '../../components/Admin/PermissionsPanel';
import VipPanel from '../../components/Admin/VipPanel';
import LinkedProfilesPanel from '../../components/Admin/LinkedProfilesPanel';
import TeamPanel from '../../components/Admin/TeamPanel';
import PerformancePanel from '../../components/Admin/PerformancePanel';
import ChangelogPanel from '../../components/Admin/ChangelogPanel';
import SupportDeskPanel from '../../components/Admin/SupportDeskPanel';
import { setAdminTabForNotifier } from '../../components/Admin/SupportChatNotifier';
import ConfirmDialog from '../../components/Admin/ConfirmDialog';
import { unlockNotifyBeep } from '../../utils/notifyBeep';
import DiscordUserChip, {
  discordAvatarUrl,
} from '../../components/Admin/DiscordUserChip';
import './Admin.css';

const MarketingFinancePanel = lazy(() =>
  import('../../components/Admin/MarketingFinancePanel')
);

const ALL_TABS = [
  { id: 'overview', label: 'Visão geral', icon: FaChartPie },
  { id: 'tickets', label: 'Tickets', icon: FaTicketAlt },
  { id: 'ratings', label: 'Avaliações', icon: FaStar },
  { id: 'support_chat', label: 'Chat VIP', icon: FaHeadset },
  { id: 'vip', label: 'VIP', icon: FaCrown },
  { id: 'marketing', label: 'Marketing & Finanças', icon: FaChartLine },
  { id: 'profiles', label: 'Contas vinculadas', icon: FaLink },
  { id: 'team', label: 'Equipe', icon: FaUsers },
  { id: 'performance', label: 'Performance', icon: FaHeartbeat },
  { id: 'messages', label: 'Mensagens', icon: FaPaperPlane },
  { id: 'welcome', label: 'Boas-vindas', icon: FaDoorOpen },
  { id: 'audit', label: 'Logs de Auditoria', icon: FaClipboardList },
  { id: 'site', label: 'Site', icon: FaSlidersH },
  { id: 'settings', label: 'Configuração', icon: FaCog },
  { id: 'changelog', label: 'Histórico', icon: FaHistory },
  { id: 'permissions', label: 'Permissões', icon: FaUserShield },
];

const FALLBACK_AUDIT_GROUPS = {
  membros: 'Membros',
  cargos: 'Cargos',
  mensagens: 'Mensagens',
  canais: 'Canais',
  voz: 'Voz',
  convites: 'Convites',
  emoji: 'Emojis & Stickers',
  servidor: 'Servidor',
};

const SITE_SECTION_LABELS = {
  homeWorld: 'Seção “Um mundo feito pra durar”',
  homeJoin: 'Seção “Como entrar”',
  homeCta: 'Seção CTA final',
  serverStatus: 'Status do servidor',
};

const SITE_PAGE_LABELS = {
  vip: 'Página Planos VIP',
  conta: 'Página Minha conta',
  nexus: 'Página /nexus',
  team: 'Página Equipe',
  economia: 'Página Economia / Arena',
  download: 'Página Download (Launcher)',
};

const STATUS_LABEL = {
  open: 'Aberto',
  closed: 'Fechado',
  deleted: 'Excluído',
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
};

const emptyType = () => ({
  id: `tipo_${Date.now().toString(36)}`,
  label: 'Novo tipo',
  description: '',
  emoji: '📋',
  color: '#5865F2',
});

const roleColorHex = (color) =>
  color > 0 ? `#${Number(color).toString(16).padStart(6, '0')}` : '#99aab5';

/**
 * Multi-select com chips + busca — usado nos filtros de auditoria.
 */
const AdminMultiSelect = ({
  label,
  hint,
  options = [],
  value = [],
  onChange,
  loading = false,
  placeholder = 'Buscar e adicionar…',
  emptyLabel = 'Nenhuma opção disponível',
  getOptionLabel,
  getOptionMeta,
  getOptionColor,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef(null);
  const inputRef = useRef(null);

  const selectedSet = useMemo(() => new Set(value.map(String)), [value]);

  const selectedOptions = useMemo(() => {
    const byId = new Map(options.map((o) => [String(o.id), o]));
    return value.map((id) => {
      const sid = String(id);
      return byId.get(sid) || { id: sid, name: sid, missing: true };
    });
  }, [options, value]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return options.filter((o) => {
      if (selectedSet.has(String(o.id))) return false;
      if (!q) return true;
      const labelText = (getOptionLabel ? getOptionLabel(o) : o.name || '').toLowerCase();
      const meta = (getOptionMeta ? getOptionMeta(o) : '') || '';
      return labelText.includes(q) || String(meta).toLowerCase().includes(q);
    });
  }, [options, selectedSet, query, getOptionLabel, getOptionMeta]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
        setQuery('');
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const add = (id) => {
    const sid = String(id);
    if (selectedSet.has(sid)) return;
    onChange([...value.map(String), sid]);
    setQuery('');
    inputRef.current?.focus();
  };

  const remove = (id) => {
    const sid = String(id);
    onChange(value.map(String).filter((x) => x !== sid));
  };

  const clearAll = () => {
    onChange([]);
  };

  return (
    <div className={`admin-ms${open ? ' is-open' : ''}`} ref={rootRef}>
      <div className="admin-ms__head">
        <span className="admin-ms__label">
          {label}
          {hint ? <small>{hint}</small> : null}
        </span>
        {value.length > 0 && (
          <button type="button" className="admin-ms__clear" onClick={clearAll}>
            Limpar
          </button>
        )}
      </div>

      <div
        className={`admin-ms__control${open ? ' is-open' : ''}${loading ? ' is-loading' : ''}`}
        onClick={() => {
          if (loading) return;
          setOpen(true);
          setTimeout(() => inputRef.current?.focus(), 0);
        }}
      >
        <div className="admin-ms__chips">
          {selectedOptions.map((opt) => {
            const color = getOptionColor ? getOptionColor(opt) : null;
            return (
              <span
                key={opt.id}
                className={`admin-ms__chip${opt.missing ? ' is-missing' : ''}`}
                style={color ? { '--chip-accent': color } : undefined}
              >
                {color ? <i className="admin-ms__dot" aria-hidden="true" /> : null}
                <span className="admin-ms__chip-text">
                  {opt.missing
                    ? `ID ${opt.id}`
                    : getOptionLabel
                      ? getOptionLabel(opt)
                      : opt.name}
                </span>
                <button
                  type="button"
                  className="admin-ms__chip-remove"
                  aria-label={`Remover ${opt.name || opt.id}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    remove(opt.id);
                  }}
                >
                  <FaTimes aria-hidden="true" />
                </button>
              </span>
            );
          })}

          <div className="admin-ms__search">
            <FaSearch className="admin-ms__search-icon" aria-hidden="true" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              disabled={loading}
              placeholder={
                loading
                  ? 'Carregando…'
                  : value.length
                    ? 'Adicionar…'
                    : placeholder
              }
              onChange={(e) => {
                setQuery(e.target.value);
                setOpen(true);
              }}
              onFocus={() => setOpen(true)}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
        <FaChevronDown className="admin-ms__chevron" aria-hidden="true" />
      </div>

      {open && !loading && (
        <div className="admin-ms__menu" role="listbox">
          {filtered.length === 0 ? (
            <p className="admin-ms__empty">
              {options.length === 0
                ? emptyLabel
                : query.trim()
                  ? 'Nenhum resultado'
                  : 'Tudo já foi selecionado'}
            </p>
          ) : (
            filtered.map((opt) => {
              const color = getOptionColor ? getOptionColor(opt) : null;
              const meta = getOptionMeta ? getOptionMeta(opt) : null;
              return (
                <button
                  key={opt.id}
                  type="button"
                  className="admin-ms__option"
                  role="option"
                  onClick={() => add(opt.id)}
                >
                  {color ? (
                    <i
                      className="admin-ms__dot"
                      style={{ '--chip-accent': color }}
                      aria-hidden="true"
                    />
                  ) : null}
                  <span className="admin-ms__option-main">
                    <strong>
                      {getOptionLabel ? getOptionLabel(opt) : opt.name}
                    </strong>
                    {meta ? <em>{meta}</em> : null}
                  </span>
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

const Admin = () => {
  const [user, setUser] = useState(() => adminService.getAdminUser());
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState('overview');
  const [supportFocusChatId, setSupportFocusChatId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [forbidden, setForbidden] = useState(false);
  const [sessionReady, setSessionReady] = useState(false);
  const [allowedTabs, setAllowedTabs] = useState([]);
  const [tabActions, setTabActions] = useState({});
  const [isOwner, setIsOwner] = useState(false);

  const [stats, setStats] = useState(null);
  const [recentOpen, setRecentOpen] = useState([]);
  const [overviewRatings, setOverviewRatings] = useState(null);
  const [overviewAudit, setOverviewAudit] = useState(null);
  const [overviewSite, setOverviewSite] = useState(null);
  const [overviewVip, setOverviewVip] = useState(null);
  const [overviewProfiles, setOverviewProfiles] = useState(null);

  const [ticketStatus, setTicketStatus] = useState('open');
  const [ticketQuery, setTicketQuery] = useState('');
  const [tickets, setTickets] = useState([]);
  const [ticketsTotal, setTicketsTotal] = useState(0);
  const [selectedNumber, setSelectedNumber] = useState(null);
  const [ticketDetail, setTicketDetail] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [channelUrl, setChannelUrl] = useState(null);
  const [ticketActionBusy, setTicketActionBusy] = useState(null);
  const [ticketReply, setTicketReply] = useState('');
  const [ticketActionMsg, setTicketActionMsg] = useState('');
  const [ticketActionError, setTicketActionError] = useState('');
  const [ticketConfirm, setTicketConfirm] = useState(null);
  const [ticketSelected, setTicketSelected] = useState([]);
  const [ticketSelectAnchor, setTicketSelectAnchor] = useState(null);
  const [ticketBulkBusy, setTicketBulkBusy] = useState(false);
  const [ticketBulkMsg, setTicketBulkMsg] = useState('');
  const [ticketBulkError, setTicketBulkError] = useState('');

  const [ratingSelected, setRatingSelected] = useState([]);
  const [ratingSelectAnchor, setRatingSelectAnchor] = useState(null);
  const [ratingBulkBusy, setRatingBulkBusy] = useState(false);
  const [ratingBulkMsg, setRatingBulkMsg] = useState('');
  const [ratingBulkError, setRatingBulkError] = useState('');

  const [settings, setSettings] = useState(null);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsMsg, setSettingsMsg] = useState('');

  const [ratingStats, setRatingStats] = useState(null);
  const [ratings, setRatings] = useState([]);

  const [siteConfig, setSiteConfig] = useState(null);
  const [siteSaving, setSiteSaving] = useState(false);
  const [siteMsg, setSiteMsg] = useState('');

  const [auditSettings, setAuditSettings] = useState(null);
  const [auditCatalog, setAuditCatalog] = useState({ events: [], groups: FALLBACK_AUDIT_GROUPS });
  const [auditSaving, setAuditSaving] = useState(false);
  const [auditMsg, setAuditMsg] = useState('');
  const [auditIgnoreChannels, setAuditIgnoreChannels] = useState([]);
  const [auditIgnoreRoles, setAuditIgnoreRoles] = useState([]);
  const [guildChannels, setGuildChannels] = useState([]);
  const [guildRoles, setGuildRoles] = useState([]);
  const [guildMembers, setGuildMembers] = useState([]);
  const [guildMembersLoading, setGuildMembersLoading] = useState(false);
  const [guildMembersError, setGuildMembersError] = useState('');
  const [guildResourcesLoading, setGuildResourcesLoading] = useState(false);

  const [welcomeSettings, setWelcomeSettings] = useState(null);
  const [welcomeTemplates, setWelcomeTemplates] = useState([]);
  const [welcomePlaceholders, setWelcomePlaceholders] = useState([]);
  const [welcomeMaxButtons, setWelcomeMaxButtons] = useState(5);
  const [welcomeSaving, setWelcomeSaving] = useState(false);
  const [welcomeMsg, setWelcomeMsg] = useState('');

  const [messageSending, setMessageSending] = useState(false);
  const [messageMsg, setMessageMsg] = useState('');
  const [messageError, setMessageError] = useState('');
  const [messageLastResult, setMessageLastResult] = useState(null);

  const [panelPermissions, setPanelPermissions] = useState(null);
  const [permissionsCatalog, setPermissionsCatalog] = useState(null);
  const [permissionsSaving, setPermissionsSaving] = useState(false);
  const [permissionsMsg, setPermissionsMsg] = useState('');

  const [vipStats, setVipStats] = useState(null);
  const [vipList, setVipList] = useState([]);
  const [vipTotal, setVipTotal] = useState(0);
  const [vipCatalog, setVipCatalog] = useState(null);
  const [vipStatus, setVipStatus] = useState('active');
  const [vipPlanFilter, setVipPlanFilter] = useState('');
  const [vipQuery, setVipQuery] = useState('');
  const [vipSelected, setVipSelected] = useState(null);
  const [vipGrantOpen, setVipGrantOpen] = useState(false);
  const [profilesLinkOpen, setProfilesLinkOpen] = useState(false);
  const [vipActionBusy, setVipActionBusy] = useState(null);
  const [vipMsg, setVipMsg] = useState('');
  const [vipError, setVipError] = useState('');
  const vipQueryRef = useRef('');

  const [profilesStats, setProfilesStats] = useState(null);
  const [profilesList, setProfilesList] = useState([]);
  const [profilesTotal, setProfilesTotal] = useState(0);
  const [profilesPendingCodes, setProfilesPendingCodes] = useState([]);
  const [profilesPresence, setProfilesPresence] = useState('all');
  const [profilesQuery, setProfilesQuery] = useState('');
  const [profilesSelected, setProfilesSelected] = useState(null);
  const [profilesActionBusy, setProfilesActionBusy] = useState(null);
  const [profilesMsg, setProfilesMsg] = useState('');
  const [profilesError, setProfilesError] = useState('');
  const profilesQueryRef = useRef('');

  const [teamStats, setTeamStats] = useState(null);
  const [teamList, setTeamList] = useState([]);
  const [teamTotal, setTeamTotal] = useState(0);
  const [teamCatalog, setTeamCatalog] = useState(null);
  const [teamVisibility, setTeamVisibility] = useState('all');
  const [teamQuery, setTeamQuery] = useState('');
  const [teamSelected, setTeamSelected] = useState(null);
  const [teamCreateOpen, setTeamCreateOpen] = useState(false);
  const [teamActionBusy, setTeamActionBusy] = useState(null);
  const [teamMsg, setTeamMsg] = useState('');
  const [teamError, setTeamError] = useState('');
  const teamQueryRef = useRef('');

  const [perfLatest, setPerfLatest] = useState(null);
  const [perfHistory, setPerfHistory] = useState([]);
  const [perfHeartbeat, setPerfHeartbeat] = useState(null);
  const [perfServers, setPerfServers] = useState([]);
  const [perfError, setPerfError] = useState('');

  const [changelogEntries, setChangelogEntries] = useState([]);
  const [changelogTotal, setChangelogTotal] = useState(0);
  const [changelogCatalog, setChangelogCatalog] = useState(null);
  const [changelogArea, setChangelogArea] = useState('');
  const [changelogQuery, setChangelogQuery] = useState('');
  const changelogQueryRef = useRef('');

  const hasSession = Boolean(user && adminService.getAccessToken());

  const visibleTabs = useMemo(() => {
    if (isOwner) return ALL_TABS;
    return ALL_TABS.filter((t) => allowedTabs.includes(t.id));
  }, [allowedTabs, isOwner]);

  const handleAuthError = useCallback((err) => {
    if (err.code === 'TAB_FORBIDDEN' || err.code === 'ACTION_FORBIDDEN') {
      // Sem permissão só nesta aba/ação — não trava o painel inteiro
      setError(
        err.message ||
          (err.code === 'ACTION_FORBIDDEN'
            ? 'Sem permissão para esta ação'
            : 'Sem permissão para esta área do painel')
      );
      setForbidden(false);
      return;
    }
    if (err.code === 'FORBIDDEN') {
      setForbidden(true);
      setError(err.message);
      return;
    }
    if (err.code === 'UNAUTHORIZED' || err.code === 'NO_TOKEN') {
      adminService.clearAdminAuth();
      setUser(null);
      setError('Sessão expirada. Entre novamente com o Discord.');
      return;
    }
    setError(err.message || 'Erro ao carregar dados');
  }, []);

  const loadOverview = useCallback(async () => {
    const data = await adminService.fetchStats();
    setStats(data.stats);
    setRecentOpen(data.recentOpen || []);
    setOverviewRatings(data.ratings || null);
    setOverviewAudit(data.audit || null);
    setOverviewSite(data.site || null);
    setOverviewVip(data.vip || null);
    setOverviewProfiles(data.profiles || null);
    if (data.admin) setUser(data.admin);
  }, []);

  const loadTickets = useCallback(async () => {
    const data = await adminService.fetchTickets({
      status: ticketStatus,
      q: ticketQuery,
      limit: 50,
    });
    setTickets(data.tickets || []);
    setTicketsTotal(data.total || 0);
    setTicketSelected([]);
    setTicketSelectAnchor(null);
  }, [ticketStatus, ticketQuery]);

  const can = useCallback(
    (tabId, action) => {
      if (isOwner) return true;
      if (!allowedTabs.includes(tabId)) return false;
      const list = tabActions?.[tabId];
      // Sem lista explícita = legado (aba liberada = tudo)
      if (!Array.isArray(list)) return true;
      return list.includes(action);
    },
    [isOwner, allowedTabs, tabActions]
  );

  const canTicketsEdit = can('tickets', 'edit');
  const canTicketsDelete = can('tickets', 'delete');
  const canRatingsDelete = can('ratings', 'delete');
  const canVipEdit = can('vip', 'edit');
  const canVipDelete = can('vip', 'delete');
  const canMarketingDelete = can('marketing', 'delete');
  // extras granulares; "edit" cobre grants antigos ainda não migrados na sessão
  const canMarketingCosts =
    can('marketing', 'costs') || can('marketing', 'edit');
  const canMarketingCoupons =
    can('marketing', 'coupons') || can('marketing', 'edit');
  const canMarketingPromos =
    can('marketing', 'promos') || can('marketing', 'edit');
  const canMarketingCreators =
    can('marketing', 'creators') || can('marketing', 'edit');
  const canMarketingExport =
    can('marketing', 'export') || can('marketing', 'edit');
  const canMarketingPricing =
    can('marketing', 'pricing') || can('marketing', 'edit');
  const canSupportChatEdit = can('support_chat', 'edit');
  const canSupportChatDelete = can('support_chat', 'delete');
  const canSupportChatChat = can('support_chat', 'chat');
  const canSupportChatClaim = can('support_chat', 'claim');
  const canSupportChatRelease = can('support_chat', 'release');
  const canSupportChatClose = can('support_chat', 'close');
  const canSupportChatTransfer = can('support_chat', 'transfer');
  const canSupportChatReport = can('support_chat', 'report');
  const canSupportChatVipGrant =
    can('support_chat', 'vip_grant') || (canSupportChatEdit && canVipEdit);
  const canProfilesEdit = can('profiles', 'edit');
  const canProfilesDelete = can('profiles', 'delete');
  const canTeamEdit = can('team', 'edit');
  const canTeamDelete = can('team', 'delete');
  const canMessagesEdit = can('messages', 'edit');
  const canWelcomeEdit = can('welcome', 'edit');
  const canAuditEdit = can('audit', 'edit');
  const canSiteEdit = can('site', 'edit');
  const canSettingsEdit = can('settings', 'edit');

  const ticketBulkEnabled =
    canTicketsDelete &&
    (ticketStatus === 'closed' ||
      ticketStatus === 'deleted' ||
      ticketStatus === 'all');

  const selectableTicketNumbers = useMemo(
    () =>
      tickets
        .filter((t) => t.status === 'closed' || t.status === 'deleted')
        .map((t) => t.ticketNumber),
    [tickets]
  );

  const allSelectableSelected =
    selectableTicketNumbers.length > 0 &&
    selectableTicketNumbers.every((n) =>
      ticketSelected.some((s) => String(s) === String(n))
    );

  const toggleTicketSelected = (ticketNumber, { shiftKey = false } = {}) => {
    const list = selectableTicketNumbers;
    const idx = list.findIndex((n) => String(n) === String(ticketNumber));
    if (idx < 0) return;

    if (shiftKey && ticketSelectAnchor != null) {
      const anchorIdx = list.findIndex(
        (n) => String(n) === String(ticketSelectAnchor)
      );
      if (anchorIdx >= 0) {
        const [from, to] =
          anchorIdx < idx ? [anchorIdx, idx] : [idx, anchorIdx];
        const range = list.slice(from, to + 1);
        setTicketSelected((prev) => {
          const next = new Set(prev.map(String));
          range.forEach((n) => next.add(String(n)));
          return list.filter((n) => next.has(String(n)));
        });
        return;
      }
    }

    setTicketSelectAnchor(ticketNumber);
    setTicketSelected((prev) => {
      const exists = prev.some((s) => String(s) === String(ticketNumber));
      if (exists) {
        return prev.filter((s) => String(s) !== String(ticketNumber));
      }
      return [...prev, ticketNumber];
    });
  };

  const toggleSelectAllTickets = () => {
    if (allSelectableSelected) {
      setTicketSelected([]);
      setTicketSelectAnchor(null);
      return;
    }
    setTicketSelected([...selectableTicketNumbers]);
    setTicketSelectAnchor(selectableTicketNumbers[0] ?? null);
  };

  const runTicketBulkDelete = async () => {
    if (!canTicketsDelete || !ticketSelected.length || ticketBulkBusy) return;
    setTicketBulkBusy(true);
    setTicketBulkMsg('');
    setTicketBulkError('');
    try {
      const result = await adminService.deleteTickets(ticketSelected);
      setTicketBulkMsg(
        `${result.deleted || 0} ticket(s) removido(s) permanentemente` +
          (result.ratingsDeleted
            ? ` · ${result.ratingsDeleted} avaliação(ões) vinculada(s)`
            : '')
      );
      setTicketConfirm(null);
      setTicketSelected([]);
      setTicketSelectAnchor(null);
      await loadTickets();
    } catch (err) {
      handleAuthError(err);
      setTicketBulkError(err.message || 'Falha ao excluir tickets');
    } finally {
      setTicketBulkBusy(false);
    }
  };

  const loadSettings = useCallback(async () => {
    const data = await adminService.fetchSettings();
    setSettings(data.settings);
  }, []);

  const loadRatings = useCallback(async () => {
    const data = await adminService.fetchRatings({ limit: 50 });
    setRatingStats(data.stats || null);
    setRatings(data.ratings || []);
    setRatingSelected([]);
    setRatingSelectAnchor(null);
  }, []);

  const allRatingsSelected =
    ratings.length > 0 &&
    ratings.every((r) =>
      ratingSelected.some((s) => String(s) === String(r.ticketNumber))
    );

  const toggleRatingSelected = (ticketNumber, { shiftKey = false } = {}) => {
    const list = ratings.map((r) => r.ticketNumber);
    const idx = list.findIndex((n) => String(n) === String(ticketNumber));
    if (idx < 0) return;

    if (shiftKey && ratingSelectAnchor != null) {
      const anchorIdx = list.findIndex(
        (n) => String(n) === String(ratingSelectAnchor)
      );
      if (anchorIdx >= 0) {
        const [from, to] =
          anchorIdx < idx ? [anchorIdx, idx] : [idx, anchorIdx];
        const range = list.slice(from, to + 1);
        setRatingSelected((prev) => {
          const next = new Set(prev.map(String));
          range.forEach((n) => next.add(String(n)));
          return list.filter((n) => next.has(String(n)));
        });
        return;
      }
    }

    setRatingSelectAnchor(ticketNumber);
    setRatingSelected((prev) => {
      const exists = prev.some((s) => String(s) === String(ticketNumber));
      if (exists) {
        return prev.filter((s) => String(s) !== String(ticketNumber));
      }
      return [...prev, ticketNumber];
    });
  };

  const toggleSelectAllRatings = () => {
    if (allRatingsSelected) {
      setRatingSelected([]);
      setRatingSelectAnchor(null);
      return;
    }
    setRatingSelected(ratings.map((r) => r.ticketNumber));
    setRatingSelectAnchor(ratings[0]?.ticketNumber ?? null);
  };

  const runRatingBulkDelete = async (mode = 'numbers') => {
    if (ratingBulkBusy) return;
    if (mode !== 'orphans' && !ratingSelected.length) return;
    setRatingBulkBusy(true);
    setRatingBulkMsg('');
    setRatingBulkError('');
    try {
      const result =
        mode === 'orphans'
          ? await adminService.deleteRatings({ mode: 'orphans' })
          : await adminService.deleteRatings({ numbers: ratingSelected });
      const removed = result.deleted || 0;
      const cleared = result.clearedOnTickets || 0;
      setRatingBulkMsg(
        mode === 'orphans'
          ? removed
            ? `${removed} avaliação(ões) órfã(s) removida(s)`
            : 'Nenhuma avaliação órfã encontrada'
          : `${removed} avaliação(ões) removida(s)` +
              (cleared ? ` · ${cleared} limpa(s) em tickets` : '')
      );
      setTicketConfirm(null);
      setRatingSelected([]);
      setRatingSelectAnchor(null);
      await loadRatings();
    } catch (err) {
      handleAuthError(err);
      setRatingBulkError(err.message || 'Falha ao excluir avaliações');
    } finally {
      setRatingBulkBusy(false);
    }
  };

  const loadSite = useCallback(async () => {
    const data = await adminService.fetchSiteConfigAdmin();
    setSiteConfig(data.config || { ...DEFAULT_SITE_CONFIG });
  }, []);

  const loadAudit = useCallback(async () => {
    setGuildResourcesLoading(true);
    try {
      const [data, resources] = await Promise.all([
        adminService.fetchAuditSettings(),
        adminService.fetchGuildResources().catch(() => null),
      ]);
      setAuditSettings(data.settings);
      if (data.catalog) {
        setAuditCatalog({
          events: data.catalog.events || [],
          groups: data.catalog.groups || FALLBACK_AUDIT_GROUPS,
        });
      }
      setAuditIgnoreChannels(
        (data.settings?.ignoreChannelIds || []).map(String)
      );
      setAuditIgnoreRoles((data.settings?.ignoreRoleIds || []).map(String));
      if (resources) {
        setGuildChannels(resources.channels || []);
        setGuildRoles(resources.roles || []);
      }
    } finally {
      setGuildResourcesLoading(false);
    }
  }, []);

  const loadWelcome = useCallback(async () => {
    setGuildResourcesLoading(true);
    try {
      const [data, resources] = await Promise.all([
        adminService.fetchWelcomeSettings(),
        adminService.fetchGuildResources().catch(() => null),
      ]);
      setWelcomeSettings(data.settings);
      setWelcomeTemplates(data.templates || []);
      setWelcomePlaceholders(data.catalog?.placeholders || []);
      setWelcomeMaxButtons(data.catalog?.maxButtons || 5);
      if (resources) {
        setGuildChannels(resources.channels || []);
        setGuildRoles(resources.roles || []);
      }
    } finally {
      setGuildResourcesLoading(false);
    }
  }, []);

  const loadGuildResources = useCallback(async () => {
    setGuildResourcesLoading(true);
    try {
      const resources = await adminService.fetchGuildResources();
      setGuildChannels(resources.channels || []);
      setGuildRoles(resources.roles || []);
      return resources;
    } finally {
      setGuildResourcesLoading(false);
    }
  }, []);

  const loadMessages = useCallback(async () => {
    setMessageError('');
    await loadGuildResources();
  }, [loadGuildResources]);

  const handleSendMessage = async (payload) => {
    setMessageSending(true);
    setMessageMsg('');
    setMessageError('');
    setMessageLastResult(null);
    try {
      const result = await adminService.sendMessage(payload);
      setMessageLastResult(result);
      setMessageMsg(
        `Mensagem enviada com sucesso em #${result.channelName || 'canal'}`
      );
    } catch (err) {
      handleAuthError(err);
      setMessageError(err.message || 'Falha ao enviar mensagem');
    } finally {
      setMessageSending(false);
    }
  };

  const loadGuildMembers = useCallback(async () => {
    setGuildMembersLoading(true);
    setGuildMembersError('');
    try {
      const membersRes = await adminService.fetchGuildMembers();
      setGuildMembers(membersRes?.members || []);
    } catch (err) {
      setGuildMembersError(
        err?.message || 'Não foi possível carregar os membros do servidor.'
      );
      setGuildMembers([]);
    } finally {
      setGuildMembersLoading(false);
    }
  }, []);

  const loadPermissions = useCallback(async () => {
    setGuildResourcesLoading(true);
    setGuildMembersLoading(true);
    setGuildMembersError('');
    try {
      const [data, resources, membersRes] = await Promise.all([
        adminService.fetchPermissions(),
        adminService.fetchGuildResources().catch(() => null),
        adminService.fetchGuildMembers().catch((err) => {
          setGuildMembersError(
            err?.message || 'Não foi possível carregar os membros do servidor.'
          );
          return null;
        }),
      ]);
      setPanelPermissions(data.settings);
      setPermissionsCatalog(data.catalog || null);
      if (resources) {
        setGuildChannels(resources.channels || []);
        setGuildRoles(resources.roles || []);
      }
      setGuildMembers(membersRes?.members || []);
    } finally {
      setGuildResourcesLoading(false);
      setGuildMembersLoading(false);
    }
  }, []);

  const loadVip = useCallback(async () => {
    setVipError('');
    const data = await adminService.fetchVip({
      status: vipStatus,
      planId: vipPlanFilter,
      q: vipQueryRef.current,
      limit: 50,
    });
    setVipStats(data.stats || null);
    setVipList(data.subscriptions || []);
    setVipTotal(data.total || 0);
    setVipCatalog(data.catalog || null);
    setVipSelected((prev) => {
      if (!prev) return null;
      const next = (data.subscriptions || []).find((s) => s.id === prev.id);
      return next || null;
    });
  }, [vipStatus, vipPlanFilter]);

  const loadProfiles = useCallback(async () => {
    setProfilesError('');
    const data = await adminService.fetchProfiles({
      presence: profilesPresence,
      q: profilesQueryRef.current,
      limit: 50,
      codes: true,
    });
    setProfilesStats(data.stats || null);
    setProfilesList(data.profiles || []);
    setProfilesTotal(data.total || 0);
    setProfilesPendingCodes(data.pendingCodes || []);
    setProfilesSelected((prev) => {
      if (!prev) return null;
      const next = (data.profiles || []).find((p) => p.id === prev.id);
      return next || null;
    });
  }, [profilesPresence]);

  const loadTeam = useCallback(async () => {
    setTeamError('');
    const data = await adminService.fetchTeam({
      visibility: teamVisibility,
      q: teamQueryRef.current,
      limit: 80,
    });
    setTeamStats(data.stats || null);
    setTeamList(data.members || []);
    setTeamTotal(data.total || 0);
    setTeamCatalog(data.catalog || null);
    setTeamSelected((prev) => {
      if (!prev) return null;
      const next = (data.members || []).find((m) => m.id === prev.id);
      return next || null;
    });
  }, [teamVisibility]);

  const loadPerformance = useCallback(async () => {
    setPerfError('');
    const data = await adminService.fetchPerformance({ hours: 2, historyLimit: 240 });
    setPerfLatest(data.latest || null);
    setPerfHistory(data.history || []);
    setPerfHeartbeat(data.heartbeat || null);
    setPerfServers(data.servers || []);
  }, []);

  const loadChangelog = useCallback(async () => {
    const data = await adminService.fetchChangelog({
      area: changelogArea,
      q: changelogQueryRef.current,
      limit: 50,
    });
    setChangelogEntries(data.entries || []);
    setChangelogTotal(data.total || 0);
    setChangelogCatalog(data.catalog || null);
  }, [changelogArea]);

  const loadSession = useCallback(async () => {
    if (!hasSession) {
      setSessionReady(false);
      return;
    }
    setLoading(true);
    setError('');
    setForbidden(false);
    try {
      const data = await adminService.fetchSession();
      const tabs = Array.isArray(data.tabs) ? data.tabs : [];
      const owner = Boolean(data.isOwner);
      // Dono sempre vê todas as abas do front (inclui novas como Equipe).
      const effectiveTabs = owner
        ? ALL_TABS.map((t) => t.id)
        : tabs;
      setAllowedTabs(effectiveTabs);
      // Sempre usa tabActions da API (inclui Chat VIP / chat, etc.)
      setTabActions(
        data.tabActions && typeof data.tabActions === 'object'
          ? data.tabActions
          : {}
      );
      setIsOwner(owner);
      if (data.user) setUser(data.user);
      setSessionReady(true);

      const caps = Array.isArray(data.capabilities) ? data.capabilities : [];
      const earlyAccessOnly =
        !effectiveTabs.length &&
        (owner || caps.includes('bypass_coming_soon'));

      if (!effectiveTabs.length && !earlyAccessOnly) {
        setForbidden(true);
        setError(
          'Acesso restrito: você não tem permissão neste painel. Peça ao dono para liberar seu cargo ou usuário.'
        );
        return;
      }

      if (earlyAccessOnly) {
        setForbidden(false);
        setError('');
        return;
      }

      setTab((current) =>
        effectiveTabs.includes(current) ? current : effectiveTabs[0] || 'overview'
      );
    } catch (err) {
      setSessionReady(false);
      handleAuthError(err);
    } finally {
      setLoading(false);
    }
  }, [hasSession, handleAuthError]);

  const refresh = useCallback(async () => {
    if (!hasSession || !sessionReady) return;
    if (!allowedTabs.includes(tab)) return;
    setLoading(true);
    setError('');
    setForbidden(false);
    try {
      if (tab === 'overview') await loadOverview();
      else if (tab === 'tickets') await loadTickets();
      else if (tab === 'ratings') await loadRatings();
      else if (tab === 'vip') await loadVip();
      else if (tab === 'profiles') await loadProfiles();
      else if (tab === 'team') await loadTeam();
      else if (tab === 'performance') await loadPerformance();
      else if (tab === 'messages') await loadMessages();
      else if (tab === 'welcome') await loadWelcome();
      else if (tab === 'audit') await loadAudit();
      else if (tab === 'site') await loadSite();
      else if (tab === 'settings') await loadSettings();
      else if (tab === 'changelog') await loadChangelog();
      else if (tab === 'permissions') await loadPermissions();
    } catch (err) {
      handleAuthError(err);
    } finally {
      setLoading(false);
    }
  }, [
    hasSession,
    sessionReady,
    allowedTabs,
    tab,
    loadOverview,
    loadTickets,
    loadRatings,
    loadVip,
    loadProfiles,
    loadTeam,
    loadPerformance,
    loadMessages,
    loadWelcome,
    loadAudit,
    loadSite,
    loadSettings,
    loadChangelog,
    loadPermissions,
    handleAuthError,
  ]);

  useEffect(() => {
    loadSession();
  }, [loadSession]);

  // Recarrega permissões ao abrir o Chat VIP (após mudanças na aba Permissões)
  useEffect(() => {
    if (!sessionReady || !hasSession || tab !== 'support_chat') return;
    let cancelled = false;
    (async () => {
      try {
        const data = await adminService.fetchSession();
        if (cancelled) return;
        if (data.tabActions && typeof data.tabActions === 'object') {
          setTabActions(data.tabActions);
        }
        if (typeof data.isOwner === 'boolean') setIsOwner(data.isOwner);
        if (Array.isArray(data.tabs)) {
          const owner = Boolean(data.isOwner);
          setAllowedTabs(owner ? ALL_TABS.map((t) => t.id) : data.tabs);
        }
      } catch {
        /* silencioso — mantém sessão atual */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tab, sessionReady, hasSession]);

  // Deep link: /admin?tab=support_chat&chat=ID (vindo do popup global)
  useEffect(() => {
    if (!sessionReady) return;
    const urlTab = String(searchParams.get('tab') || '').trim();
    const urlChat = String(searchParams.get('chat') || '').trim();
    const canTab = (id) => isOwner || allowedTabs.includes(id);

    if (urlChat && canTab('support_chat')) {
      setTab('support_chat');
      setSupportFocusChatId(urlChat);
      return;
    }
    if (urlTab && canTab(urlTab)) {
      setTab(urlTab);
    }
  }, [sessionReady, searchParams, allowedTabs, isOwner]);

  useEffect(() => {
    setAdminTabForNotifier(tab);
  }, [tab]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (
      (!vipGrantOpen && !teamCreateOpen && !profilesLinkOpen && tab !== 'marketing') ||
      !hasSession ||
      !sessionReady
    ) {
      return;
    }
    loadGuildMembers().catch(handleAuthError);
  }, [
    vipGrantOpen,
    teamCreateOpen,
    profilesLinkOpen,
    tab,
    hasSession,
    sessionReady,
    loadGuildMembers,
    handleAuthError,
  ]);

  const openTicketDetail = async (number) => {
    setSelectedNumber(number);
    setTicketDetail(null);
    setTimeline([]);
    setChannelUrl(null);
    setTicketReply('');
    setTicketActionMsg('');
    setTicketActionError('');
    setTicketConfirm(null);
    setLoading(true);
    setError('');
    try {
      const data = await adminService.fetchTicket(number);
      setTicketDetail(data.ticket);
      setTimeline(data.timeline || []);
      setChannelUrl(data.channelUrl);
    } catch (err) {
      handleAuthError(err);
    } finally {
      setLoading(false);
    }
  };

  const refreshTicketDetail = async (number = selectedNumber) => {
    if (number == null) return;
    try {
      const data = await adminService.fetchTicket(number);
      setTicketDetail(data.ticket);
      setTimeline(data.timeline || []);
      setChannelUrl(data.channelUrl);
    } catch (err) {
      handleAuthError(err);
    }
  };

  const runTicketAction = async (action, { content } = {}) => {
    if (!canTicketsEdit || !selectedNumber || ticketActionBusy) return;
    setTicketActionBusy(action);
    setTicketActionMsg('');
    setTicketActionError('');
    try {
      const result = await adminService.ticketAction({
        action,
        number: selectedNumber,
        content,
      });
      const labels = {
        claim: 'Ticket assumido.',
        unclaim: 'Ticket liberado.',
        close: 'Ticket fechado.',
        reopen: 'Ticket reaberto.',
        reply: 'Mensagem enviada no canal.',
        transcript: 'Transcrição gerada.',
      };
      const key = result.action || action;
      setTicketActionMsg(labels[key] || 'Ação concluída.');
      if (action === 'reply') setTicketReply('');
      if (result.transcriptUrl) {
        window.open(result.transcriptUrl, '_blank', 'noopener,noreferrer');
      }
      await refreshTicketDetail(selectedNumber);
      await loadTickets();
    } catch (err) {
      handleAuthError(err);
      setTicketActionError(err.message || 'Falha na ação do ticket');
    } finally {
      setTicketActionBusy(null);
      setTicketConfirm(null);
    }
  };

  const closeDetail = () => {
    setSelectedNumber(null);
    setTicketDetail(null);
    setTimeline([]);
    setChannelUrl(null);
    setTicketReply('');
    setTicketActionMsg('');
    setTicketActionError('');
    setTicketConfirm(null);
  };

  const login = () => {
    window.location.href = discordService.getAuthUrl('admin');
  };

  const logout = () => {
    adminService.clearAdminAuth();
    setUser(null);
    setSessionReady(false);
    setAllowedTabs([]);
    setTabActions({});
    setIsOwner(false);
    setStats(null);
    setTickets([]);
    setSettings(null);
    setRatings([]);
    setRatingStats(null);
    setSiteConfig(null);
    setAuditSettings(null);
    setWelcomeSettings(null);
    setPanelPermissions(null);
    setVipList([]);
    setVipStats(null);
    setVipSelected(null);
    setPerfLatest(null);
    setPerfHistory([]);
    setPerfHeartbeat(null);
    setPerfServers([]);
    setPerfError('');
  };

  const updateSettingField = (key, value) => {
    setSettings((prev) => (prev ? { ...prev, [key]: value } : prev));
  };

  const updateType = (index, key, value) => {
    setSettings((prev) => {
      if (!prev) return prev;
      const types = [...(prev.types || [])];
      types[index] = { ...types[index], [key]: value };
      return { ...prev, types };
    });
  };

  const addType = () => {
    setSettings((prev) => {
      if (!prev) return prev;
      const types = [...(prev.types || []), emptyType()];
      return { ...prev, types };
    });
  };

  const removeType = (index) => {
    setSettings((prev) => {
      if (!prev) return prev;
      const types = (prev.types || []).filter((_, i) => i !== index);
      return { ...prev, types };
    });
  };

  const saveSettings = async () => {
    if (!settings) return;
    setSettingsSaving(true);
    setSettingsMsg('');
    setError('');
    try {
      const payload = {
        staffRoleId: settings.staffRoleId,
        categoryId: settings.categoryId,
        logChannelId: settings.logChannelId,
        maxOpenTickets: settings.maxOpenTickets,
        naming: settings.naming,
        welcomeMessage: settings.welcomeMessage,
        panelTitle: settings.panelTitle,
        panelDescription: settings.panelDescription,
        types: settings.types,
      };
      const data = await adminService.updateSettings(payload);
      setSettings(data.settings);
      if (data.panelSync?.synced) {
        setSettingsMsg('Configuração salva e painel do Discord atualizado.');
      } else {
        setSettingsMsg(
          'Configuração salva. Se o painel no Discord não atualizou, republicar com /ticket.'
        );
      }
    } catch (err) {
      handleAuthError(err);
    } finally {
      setSettingsSaving(false);
    }
  };

  const updateSiteToggle = (group, key, value) => {
    setSiteConfig((prev) => {
      if (!prev) return prev;
      if (group === 'root') {
        return { ...prev, [key]: value };
      }
      return {
        ...prev,
        [group]: {
          ...(prev[group] || {}),
          [key]: value,
        },
      };
    });
  };

  const saveSiteConfig = async () => {
    if (!siteConfig) return;
    setSiteSaving(true);
    setSiteMsg('');
    setError('');
    try {
      const data = await adminService.updateSiteConfig({
        comingSoon: siteConfig.comingSoon,
        sections: siteConfig.sections,
        pages: siteConfig.pages,
      });
      setSiteConfig(data.config);
      setSiteConfigCache(data.config);
      setSiteMsg('Configuração do site salva.');
    } catch (err) {
      handleAuthError(err);
    } finally {
      setSiteSaving(false);
    }
  };

  const updateAuditField = (key, value) => {
    setAuditSettings((prev) => (prev ? { ...prev, [key]: value } : prev));
  };

  const updateAuditEvent = (eventId, value) => {
    setAuditSettings((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        events: {
          ...(prev.events || {}),
          [eventId]: value,
        },
      };
    });
  };

  const setAllAuditEvents = (value) => {
    setAuditSettings((prev) => {
      if (!prev) return prev;
      const events = { ...(prev.events || {}) };
      for (const def of auditCatalog.events || []) {
        events[def.id] = value;
      }
      return { ...prev, events };
    });
  };

  const setGroupAuditEvents = (group, value) => {
    setAuditSettings((prev) => {
      if (!prev) return prev;
      const events = { ...(prev.events || {}) };
      for (const def of auditCatalog.events || []) {
        if (def.group === group) events[def.id] = value;
      }
      return { ...prev, events };
    });
  };

  const setAuditIgnoreChannelsList = (ids) => {
    setAuditIgnoreChannels(ids.map(String));
  };

  const setAuditIgnoreRolesList = (ids) => {
    setAuditIgnoreRoles(ids.map(String));
  };

  const saveAuditSettings = async () => {
    if (!auditSettings) return;
    setAuditSaving(true);
    setAuditMsg('');
    setError('');
    try {
      const data = await adminService.updateAuditSettings({
        enabled: Boolean(auditSettings.enabled),
        channelId: auditSettings.channelId,
        ignoreBots: Boolean(auditSettings.ignoreBots),
        ignoreSelf: Boolean(auditSettings.ignoreSelf),
        showExecutor: Boolean(auditSettings.showExecutor),
        events: auditSettings.events || {},
        ignoreChannelIds: auditIgnoreChannels,
        ignoreRoleIds: auditIgnoreRoles,
      });
      setAuditSettings(data.settings);
      if (data.catalog) {
        setAuditCatalog({
          events: data.catalog.events || [],
          groups: data.catalog.groups || FALLBACK_AUDIT_GROUPS,
        });
      }
      setAuditIgnoreChannels(
        (data.settings?.ignoreChannelIds || []).map(String)
      );
      setAuditIgnoreRoles((data.settings?.ignoreRoleIds || []).map(String));
      setAuditMsg('Logs de auditoria salvos. O bot aplica em até ~15s.');
    } catch (err) {
      handleAuthError(err);
    } finally {
      setAuditSaving(false);
    }
  };

  const saveWelcomeSettings = async () => {
    if (!welcomeSettings) return;
    setWelcomeSaving(true);
    setWelcomeMsg('');
    setError('');
    try {
      const data = await adminService.updateWelcomeSettings({
        enabled: Boolean(welcomeSettings.enabled),
        channelId: welcomeSettings.channelId,
        ignoreBots: Boolean(welcomeSettings.ignoreBots),
        mentionUser: Boolean(welcomeSettings.mentionUser),
        templateId: welcomeSettings.templateId,
        embed: welcomeSettings.embed,
        buttons: welcomeSettings.buttons,
      });
      setWelcomeSettings(data.settings);
      if (data.templates) setWelcomeTemplates(data.templates);
      if (data.catalog?.placeholders) {
        setWelcomePlaceholders(data.catalog.placeholders);
      }
      if (data.catalog?.maxButtons) {
        setWelcomeMaxButtons(data.catalog.maxButtons);
      }
      setWelcomeMsg('Boas-vindas salvas. O bot aplica em até ~15s.');
    } catch (err) {
      handleAuthError(err);
    } finally {
      setWelcomeSaving(false);
    }
  };

  const applyWelcomeTemplate = async (templateId) => {
    setWelcomeMsg('');
    setError('');
    try {
      const data = await adminService.updateWelcomeSettings({
        applyTemplate: templateId,
      });
      setWelcomeSettings(data.settings);
      if (data.templates) setWelcomeTemplates(data.templates);
      setWelcomeMsg(`Template “${templateId}” aplicado e salvo. O bot usa em até ~15s.`);
    } catch (err) {
      handleAuthError(err);
      throw err;
    }
  };

  const savePermissions = async () => {
    if (!panelPermissions) return;
    setPermissionsSaving(true);
    setPermissionsMsg('');
    setError('');
    try {
      const data = await adminService.updatePermissions({
        roleGrants: panelPermissions.roleGrants || [],
        userGrants: panelPermissions.userGrants || [],
      });
      setPanelPermissions(data.settings);
      if (data.catalog) setPermissionsCatalog(data.catalog);
      // Atualiza a sessão atual para refletir Chat / ações novas sem F5
      try {
        const session = await adminService.fetchSession();
        if (session.tabActions && typeof session.tabActions === 'object') {
          setTabActions(session.tabActions);
        }
        if (typeof session.isOwner === 'boolean') setIsOwner(session.isOwner);
        if (Array.isArray(session.tabs)) {
          const owner = Boolean(session.isOwner);
          setAllowedTabs(owner ? ALL_TABS.map((t) => t.id) : session.tabs);
        }
      } catch {
        /* ok */
      }
      setPermissionsMsg(
        'Permissões salvas. A sessão atual já foi atualizada; outros staffs precisam reabrir o painel ou a aba Chat VIP.'
      );
    } catch (err) {
      handleAuthError(err);
    } finally {
      setPermissionsSaving(false);
    }
  };

  const runVipAction = async (action, payload = {}) => {
    setVipActionBusy(action);
    setVipMsg('');
    setVipError('');
    try {
      const data = await adminService.updateVip({ action, ...payload });
      setVipMsg(data.message || 'Ação VIP concluída.');
      if (action === 'delete') {
        setVipSelected(null);
      } else if (data.subscription) {
        setVipSelected(data.subscription);
      }
      await loadVip();
      if (data.sync) {
        const parts = [];
        const lpStatus = data.sync.lp || data.sync.rcon;
        const lpOk = !lpStatus || lpStatus === 'ok' || lpStatus === 'queued';

        if (data.sync.discord && data.sync.discord !== 'ok') {
          parts.push(`Discord: ${data.sync.discord}`);
        }
        if (!lpOk) {
          parts.push(`LuckPerms: ${lpStatus}`);
        }
        if (parts.length) {
          setVipError(`Sincronização parcial — ${parts.join(' · ')}`);
        } else if (lpStatus === 'queued') {
          setVipMsg(
            (data.message || 'Ação VIP concluída.') +
              ' LuckPerms na fila do NexusVIP — aplica no servidor em ~15s.'
          );
        }
      }
      if (action === 'grant') setVipGrantOpen(false);
    } catch (err) {
      if (err.code === 'FORBIDDEN' || err.code === 'UNAUTHORIZED' || err.code === 'NO_TOKEN') {
        handleAuthError(err);
      } else {
        setVipError(err.message || 'Falha na ação VIP');
      }
    } finally {
      setVipActionBusy(null);
    }
  };

  const runProfilesAction = async (action, payload = {}) => {
    setProfilesActionBusy(action);
    setProfilesMsg('');
    setProfilesError('');
    try {
      const data = await adminService.updateProfiles({ action, ...payload });
      setProfilesMsg(data.message || 'Ação concluída.');
      if (action === 'unlink') {
        setProfilesSelected(null);
      } else if (action === 'link') {
        setProfilesLinkOpen(false);
        if (data.profile) setProfilesSelected(data.profile);
      } else if (data.profile) {
        setProfilesSelected(data.profile);
      }
      await loadProfiles();
    } catch (err) {
      if (err.code === 'FORBIDDEN' || err.code === 'UNAUTHORIZED' || err.code === 'NO_TOKEN') {
        handleAuthError(err);
      } else {
        setProfilesError(err.message || 'Falha na ação de vínculo');
      }
    } finally {
      setProfilesActionBusy(null);
    }
  };

  const runTeamAction = async (action, payload = {}) => {
    setTeamActionBusy(action);
    setTeamMsg('');
    setTeamError('');
    try {
      const data = await adminService.updateTeam({ action, ...payload });
      setTeamMsg(data.message || 'Ação concluída.');
      if (data.catalog) setTeamCatalog(data.catalog);
      if (data.stats) setTeamStats(data.stats);

      if (action === 'delete') {
        setTeamSelected(null);
      } else if (data.member) {
        setTeamSelected(data.member);
      }
      if (action === 'create') setTeamCreateOpen(false);

      if (action === 'reorder' && data.members) {
        setTeamList(data.members);
      } else if (action === 'create_badge' || action === 'update_badge') {
        // catálogo já veio no response — não recarrega pra não resetar o formulário
      } else if (action !== 'reorder') {
        await loadTeam();
      }
      return data;
    } catch (err) {
      if (err.code === 'FORBIDDEN' || err.code === 'UNAUTHORIZED' || err.code === 'NO_TOKEN') {
        handleAuthError(err);
      } else if (err.code === 'TAB_FORBIDDEN') {
        setTeamError(err.message || 'Sem permissão para gerenciar a equipe');
      } else {
        setTeamError(err.message || 'Falha na ação da equipe');
      }
      return null;
    } finally {
      setTeamActionBusy(null);
    }
  };

  const auditEventsByGroup = useMemo(() => {
    const groups = auditCatalog.groups || FALLBACK_AUDIT_GROUPS;
    const map = {};
    for (const key of Object.keys(groups)) {
      map[key] = [];
    }
    for (const def of auditCatalog.events || []) {
      if (!map[def.group]) map[def.group] = [];
      map[def.group].push(def);
    }
    return map;
  }, [auditCatalog]);

  const auditEnabledCount = useMemo(() => {
    if (!auditSettings?.events) return 0;
    return Object.values(auditSettings.events).filter(Boolean).length;
  }, [auditSettings]);

  const displayName = useMemo(() => {
    if (!user) return '';
    return user.global_name || user.username || 'Admin';
  }, [user]);

  if (!hasSession) {
    return (
      <div className="admin">
        <Navbar />
        <div className="admin-gate">
          <div className="admin-gate__glow" aria-hidden="true" />
          <div className="admin-gate__card">
            <p className="admin-gate__kicker">Painel de gerenciamento</p>
            <h1 className="admin-gate__title">Admin Nexus</h1>
            <p className="admin-gate__copy">
              Área restrita da staff Nexus. Entre com Discord — o dono libera
              cargos e usuários nas Permissões.
            </p>
            {error && <p className="admin-gate__error">{error}</p>}
            <div className="admin-gate__actions">
              <button type="button" className="admin-btn admin-btn--primary" onClick={login}>
                <FaDiscord aria-hidden="true" />
                Entrar com Discord
              </button>
              <Link to="/" className="admin-gate__home">
                Voltar ao site
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (forbidden) {
    return (
      <div className="admin">
        <Navbar />
        <div className="admin-gate">
          <div className="admin-gate__glow" aria-hidden="true" />
          <div className="admin-gate__card">
            <FaLock className="admin-gate__lock" aria-hidden="true" />
            <h1 className="admin-gate__title">Acesso restrito</h1>
            <p className="admin-gate__copy">
              {error ||
                'Você não tem permissão neste painel. Peça ao dono para liberar seu cargo ou usuário.'}
            </p>
            <div className="admin-gate__actions">
              <button type="button" className="admin-btn admin-btn--ghost" onClick={logout}>
                Sair
              </button>
              <Link to="/" className="admin-gate__home">
                Voltar ao site
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Só acesso antecipado (sem abas do painel): sessão válida, site liberado
  if (sessionReady && !allowedTabs.length) {
    return (
      <div className="admin">
        <Navbar />
        <div className="admin-gate">
          <div className="admin-gate__glow" aria-hidden="true" />
          <div className="admin-gate__card">
            <p className="admin-gate__kicker">Acesso antecipado</p>
            <h1 className="admin-gate__title">Site liberado</h1>
            <p className="admin-gate__copy">
              Sua conta pode navegar no site público com o Coming Soon ativo.
              Você ainda não tem abas do painel — peça ao dono se precisar
              gerenciar o servidor.
            </p>
            <div className="admin-gate__actions">
              <Link to="/" className="admin-btn admin-btn--primary">
                Ir para o site
              </Link>
              <button type="button" className="admin-btn admin-btn--ghost" onClick={logout}>
                Sair
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!sessionReady) {
    return (
      <div className="admin">
        <Navbar />
        <div className="admin-gate">
          <div className="admin-gate__glow" aria-hidden="true" />
          <div className="admin-gate__card">
            <p className="admin-gate__kicker">Painel de gerenciamento</p>
            <h1 className="admin-gate__title">
              {error ? 'Falha ao entrar' : 'Carregando…'}
            </h1>
            <p className="admin-gate__copy">
              {error || 'Validando suas permissões no servidor.'}
            </p>
            {error && (
              <div className="admin-gate__actions">
                <button
                  type="button"
                  className="admin-btn admin-btn--primary"
                  onClick={loadSession}
                >
                  Tentar de novo
                </button>
                <button type="button" className="admin-btn admin-btn--ghost" onClick={logout}>
                  Sair
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin" onPointerDown={unlockNotifyBeep}>
      <Navbar />
      <div className="admin-shell">
        <aside className="admin-sidebar">
          <div className="admin-sidebar__brand">
            <span className="admin-sidebar__kicker">Nexus</span>
            <strong>Painel Admin</strong>
          </div>

          <div className="admin-sidebar__user">
            {discordAvatarUrl(user) && (
              <img
                src={discordAvatarUrl(user)}
                alt=""
                className="admin-sidebar__avatar"
                width={40}
                height={40}
              />
            )}
            <div>
              <p className="admin-sidebar__name">{displayName}</p>
              <p className="admin-sidebar__role">
                {isOwner ? 'Dono' : 'Staff'}
              </p>
            </div>
          </div>

          <nav className="admin-nav" aria-label="Seções do painel">
            {visibleTabs.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                type="button"
                className={`admin-nav__item${tab === id ? ' admin-nav__item--active' : ''}`}
                onClick={() => {
                  closeDetail();
                  setTab(id);
                }}
              >
                <Icon aria-hidden="true" />
                {label}
              </button>
            ))}
          </nav>

          <div className="admin-sidebar__footer">
            <button type="button" className="admin-nav__item" onClick={refresh} disabled={loading}>
              <FaSync className={loading ? 'admin-spin' : ''} aria-hidden="true" />
              Atualizar
            </button>
            <button type="button" className="admin-nav__item" onClick={logout}>
              <FaSignOutAlt aria-hidden="true" />
              Sair
            </button>
          </div>
        </aside>

        <main className="admin-main">
          {error && !forbidden && (
            <div className="admin-alert" role="alert">
              {error}
            </div>
          )}

          {tab === 'overview' && (
            <section className="admin-section">
              <header className="admin-section__head">
                <h1>Visão geral</h1>
                <p>
                  Painel do servidor
                  {user?.global_name || user?.username
                    ? ` · olá, ${user.global_name || user.username}`
                    : ''}
                </p>
              </header>

              <div className="admin-stats admin-stats--wide">
                <div className="admin-stat">
                  <span className="admin-stat__label">Tickets abertos</span>
                  <strong className="admin-stat__value">{stats?.open ?? '—'}</strong>
                  <span className="admin-stat__sub">
                    {stats?.unclaimed != null
                      ? `${stats.unclaimed} sem claim`
                      : '—'}
                  </span>
                </div>
                <div className="admin-stat">
                  <span className="admin-stat__label">VIP ativos</span>
                  <strong className="admin-stat__value">
                    {overviewVip?.active ?? '—'}
                  </strong>
                  <span className="admin-stat__sub">
                    {overviewVip?.newLast7 != null
                      ? `+${overviewVip.newLast7} em 7 dias`
                      : '—'}
                  </span>
                </div>
                <div className="admin-stat">
                  <span className="admin-stat__label">Contas vinculadas</span>
                  <strong className="admin-stat__value">
                    {overviewProfiles?.total ?? '—'}
                  </strong>
                  <span className="admin-stat__sub">
                    {overviewProfiles?.online != null
                      ? `${overviewProfiles.online} online agora`
                      : '—'}
                  </span>
                </div>
                <div className="admin-stat">
                  <span className="admin-stat__label">Avaliação média</span>
                  <strong className="admin-stat__value">
                    {overviewRatings?.average != null
                      ? overviewRatings.average.toFixed(1)
                      : '—'}
                  </strong>
                  <span className="admin-stat__sub">
                    {overviewRatings?.total != null
                      ? `${overviewRatings.total} avaliações`
                      : '—'}
                  </span>
                </div>
                <div className="admin-stat">
                  <span className="admin-stat__label">Auditoria</span>
                  <strong className="admin-stat__value admin-stat__value--sm">
                    {overviewAudit
                      ? overviewAudit.enabled
                        ? 'Ativa'
                        : 'Off'
                      : '—'}
                  </strong>
                  <span className="admin-stat__sub">
                    {overviewAudit
                      ? `${overviewAudit.enabledEvents}/${overviewAudit.totalEvents} eventos`
                      : '—'}
                  </span>
                </div>
                <div className="admin-stat">
                  <span className="admin-stat__label">Site</span>
                  <strong className="admin-stat__value admin-stat__value--sm">
                    {overviewSite
                      ? overviewSite.comingSoon
                        ? 'Em breve'
                        : 'Online'
                      : '—'}
                  </strong>
                  <span className="admin-stat__sub">
                    {stats?.openLast7 != null
                      ? `${stats.openLast7} tickets / 7d`
                      : '—'}
                  </span>
                </div>
                <div className="admin-stat">
                  <span className="admin-stat__label">Tickets (total)</span>
                  <strong className="admin-stat__value">{stats?.total ?? '—'}</strong>
                  <span className="admin-stat__sub">
                    {stats?.closedLast7 != null
                      ? `${stats.closedLast7} fechados / 7d`
                      : '—'}
                  </span>
                </div>
              </div>

              <div className="admin-dash">
                <div className="admin-panel">
                  <div className="admin-panel__head">
                    <h2>
                      <FaTicketAlt aria-hidden="true" /> Tickets abertos
                    </h2>
                    <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                      {(isOwner || allowedTabs.includes('support_chat')) && (
                        <button
                          type="button"
                          className="admin-btn admin-btn--ghost admin-btn--sm"
                          onClick={() => setTab('support_chat')}
                        >
                          <FaHeadset aria-hidden="true" /> Chat VIP
                        </button>
                      )}
                      <button
                        type="button"
                        className="admin-btn admin-btn--ghost admin-btn--sm"
                        onClick={() => setTab('tickets')}
                      >
                        Ver todos
                      </button>
                    </div>
                  </div>
                  {recentOpen.length === 0 ? (
                    <p className="admin-empty">Nenhum ticket aberto no momento.</p>
                  ) : (
                    <ul className="admin-list">
                      {recentOpen.map((t) => (
                        <li key={t.ticketNumber}>
                          <button
                            type="button"
                            className="admin-list__row"
                            onClick={() => {
                              setTab('tickets');
                              openTicketDetail(t.ticketNumber);
                            }}
                          >
                            <span className="admin-list__id">#{t.ticketNumber}</span>
                            <span className="admin-list__main">
                              {t.typeEmoji} {t.assunto || t.typeLabel}
                            </span>
                            <span className="admin-list__meta">
                              {t.claimedBy ? 'Claimed' : 'Livre'} · {t.userTag}
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div className="admin-panel">
                  <div className="admin-panel__head">
                    <h2>
                      <FaCrown aria-hidden="true" /> VIP recentes
                    </h2>
                  </div>
                  {!overviewVip ? (
                    <p className="admin-empty">Não foi possível carregar os VIPs.</p>
                  ) : overviewVip.recent?.length ? (
                    <>
                      {overviewVip.byPlan &&
                        Object.keys(overviewVip.byPlan).length > 0 && (
                          <div className="admin-vip-plans">
                            {Object.values(overviewVip.byPlan).map((p) => (
                              <span key={p.id} className="admin-vip-plans__item">
                                <strong>{p.count}</strong> {p.name.replace(/^Nexus VIP /, '')}
                              </span>
                            ))}
                          </div>
                        )}
                      <ul className="admin-list">
                        {overviewVip.recent.map((v, i) => (
                          <li key={`${v.discordId}-${i}`}>
                            <div className="admin-list__row admin-list__row--static">
                              <span className="admin-list__id admin-list__id--wide">
                                {v.minecraftNick || '—'}
                              </span>
                              <span className="admin-list__main">
                                {(v.planName || v.planId || '').replace(/^Nexus VIP /, '')}
                                {v.billing === 'annual' ? ' · anual' : ' · mensal'}
                              </span>
                              <span className="admin-list__meta">
                                {formatDate(v.createdAt)}
                              </span>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </>
                  ) : (
                    <p className="admin-empty">Nenhuma assinatura VIP ativa.</p>
                  )}
                </div>

                <div className="admin-panel">
                  <div className="admin-panel__head">
                    <h2>
                      <FaStar aria-hidden="true" /> Últimas avaliações
                    </h2>
                    <button
                      type="button"
                      className="admin-btn admin-btn--ghost admin-btn--sm"
                      onClick={() => setTab('ratings')}
                    >
                      Ver todas
                    </button>
                  </div>
                  {!overviewRatings?.recent?.length ? (
                    <p className="admin-empty">Ainda sem avaliações.</p>
                  ) : (
                    <ul className="admin-list">
                      {overviewRatings.recent.map((r) => (
                        <li key={r.ticketNumber}>
                          <button
                            type="button"
                            className="admin-list__row"
                            onClick={() => {
                              setTab('tickets');
                              openTicketDetail(r.ticketNumber);
                            }}
                          >
                            <span className="admin-list__id">#{r.ticketNumber}</span>
                            <span className="admin-list__main">
                              {'★'.repeat(r.stars)}
                              {'☆'.repeat(5 - r.stars)}
                              {r.comment ? ` · ${r.comment}` : ''}
                            </span>
                            <span className="admin-list__meta">
                              {r.userTag || '—'}
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div className="admin-panel">
                  <div className="admin-panel__head">
                    <h2>
                      <FaSlidersH aria-hidden="true" /> Status do sistema
                    </h2>
                  </div>
                  <ul className="admin-status-list">
                    <li>
                      <button
                        type="button"
                        className="admin-status-list__row"
                        onClick={() => setTab('audit')}
                      >
                        <span className="admin-status-list__icon">
                          <FaClipboardList aria-hidden="true" />
                        </span>
                        <span className="admin-status-list__body">
                          <strong>Logs de auditoria</strong>
                          <small>
                            {overviewAudit?.enabled
                              ? `Ativo · ${overviewAudit.enabledEvents} eventos`
                              : 'Desativado'}
                            {overviewAudit?.channelId
                              ? ' · canal configurado'
                              : ' · sem canal'}
                          </small>
                        </span>
                        {overviewAudit?.enabled ? (
                          <FaCheckCircle className="admin-status-list__ok" aria-hidden="true" />
                        ) : (
                          <FaExclamationCircle
                            className="admin-status-list__warn"
                            aria-hidden="true"
                          />
                        )}
                      </button>
                    </li>
                    <li>
                      <button
                        type="button"
                        className="admin-status-list__row"
                        onClick={() => setTab('site')}
                      >
                        <span className="admin-status-list__icon">
                          <FaGlobe aria-hidden="true" />
                        </span>
                        <span className="admin-status-list__body">
                          <strong>Site / landing</strong>
                          <small>
                            {overviewSite?.comingSoon
                              ? 'Modo coming soon ligado'
                              : 'Site liberado ao público'}
                            {overviewSite?.pages?.vip === false
                              ? ' · VIP oculto'
                              : ''}
                          </small>
                        </span>
                        {overviewSite?.comingSoon ? (
                          <FaExclamationCircle
                            className="admin-status-list__warn"
                            aria-hidden="true"
                          />
                        ) : (
                          <FaCheckCircle className="admin-status-list__ok" aria-hidden="true" />
                        )}
                      </button>
                    </li>
                    <li>
                      <button
                        type="button"
                        className="admin-status-list__row"
                        onClick={() => setTab('settings')}
                      >
                        <span className="admin-status-list__icon">
                          <FaCog aria-hidden="true" />
                        </span>
                        <span className="admin-status-list__body">
                          <strong>Tickets / painel Discord</strong>
                          <small>
                            {stats?.closed != null
                              ? `${stats.closed} fechados · ${stats.deleted || 0} excluídos`
                              : 'Abrir configuração'}
                          </small>
                        </span>
                        <FaChevronDown
                          className="admin-status-list__chevron"
                          aria-hidden="true"
                        />
                      </button>
                    </li>
                    <li>
                      <button
                        type="button"
                        className="admin-status-list__row"
                        onClick={() => setTab('ratings')}
                      >
                        <span className="admin-status-list__icon">
                          <FaStar aria-hidden="true" />
                        </span>
                        <span className="admin-status-list__body">
                          <strong>Satisfação do atendimento</strong>
                          <small>
                            {overviewRatings?.total
                              ? `Média ${overviewRatings.average.toFixed(1)} · ${overviewRatings.withComments} com comentário`
                              : 'Sem avaliações ainda'}
                          </small>
                        </span>
                        <FaChevronDown
                          className="admin-status-list__chevron"
                          aria-hidden="true"
                        />
                      </button>
                    </li>
                  </ul>
                </div>
              </div>
            </section>
          )}

          {tab === 'tickets' && (
            <section className="admin-section">
              {!selectedNumber ? (
                <>
                  <header className="admin-section__head">
                    <h1>Tickets</h1>
                    <p>
                      {ticketsTotal} resultado{ticketsTotal === 1 ? '' : 's'}
                    </p>
                  </header>

                  <div className="admin-toolbar">
                    <div className="admin-filters">
                      {['open', 'closed', 'deleted', 'all'].map((s) => (
                        <button
                          key={s}
                          type="button"
                          className={`admin-chip${ticketStatus === s ? ' admin-chip--active' : ''}`}
                          onClick={() => {
                            setTicketStatus(s);
                            setTicketSelected([]);
                            setTicketSelectAnchor(null);
                            setTicketBulkMsg('');
                            setTicketBulkError('');
                          }}
                        >
                          {s === 'all' ? 'Todos' : STATUS_LABEL[s]}
                        </button>
                      ))}
                    </div>
                    <form
                      className="admin-search"
                      onSubmit={(e) => {
                        e.preventDefault();
                        refresh();
                      }}
                    >
                      <input
                        type="search"
                        placeholder="Buscar nº, autor, assunto..."
                        value={ticketQuery}
                        onChange={(e) => setTicketQuery(e.target.value)}
                      />
                      <button type="submit" className="admin-btn admin-btn--primary admin-btn--sm">
                        Buscar
                      </button>
                    </form>
                  </div>

                  {ticketBulkEnabled && (
                    <div className="admin-bulkbar">
                      <label className="admin-bulkbar__select-all">
                        <input
                          type="checkbox"
                          className="admin-check"
                          checked={allSelectableSelected}
                          disabled={
                            selectableTicketNumbers.length === 0 || ticketBulkBusy
                          }
                          onChange={toggleSelectAllTickets}
                        />
                        <span>
                          {ticketSelected.length > 0
                            ? `${ticketSelected.length} selecionado${
                                ticketSelected.length === 1 ? '' : 's'
                              }`
                            : 'Selecionar tudo'}
                        </span>
                      </label>
                      <p className="admin-bulkbar__hint">
                        Marque tickets fechados/excluídos para limpar o histórico.
                        Shift+clique seleciona um intervalo.
                      </p>
                      <button
                        type="button"
                        className="admin-btn admin-btn--danger admin-btn--sm"
                        disabled={!ticketSelected.length || ticketBulkBusy}
                        onClick={() =>
                          setTicketConfirm({
                            kind: 'bulk-delete',
                            title: 'Excluir tickets permanentemente?',
                            message: `Isso remove ${ticketSelected.length} ticket(s) do banco de dados (histórico e avaliações vinculadas). Não dá para desfazer.`,
                            confirmLabel: 'Excluir do banco',
                            tone: 'danger',
                          })
                        }
                      >
                        <FaTrash aria-hidden="true" />
                        {ticketBulkBusy ? 'Excluindo…' : 'Excluir selecionados'}
                      </button>
                    </div>
                  )}

                  {(ticketBulkMsg || ticketBulkError) && (
                    <div className="admin-bulkbar__feedback">
                      {ticketBulkMsg && (
                        <p className="admin-flash admin-flash--ok">{ticketBulkMsg}</p>
                      )}
                      {ticketBulkError && (
                        <p className="admin-flash admin-flash--err">{ticketBulkError}</p>
                      )}
                    </div>
                  )}

                  <div className="admin-table-wrap">
                    <table className="admin-table">
                      <thead>
                        <tr>
                          {ticketBulkEnabled && (
                            <th className="admin-table__check">
                              <input
                                type="checkbox"
                                className="admin-check"
                                aria-label="Selecionar todos os tickets elegíveis"
                                checked={allSelectableSelected}
                                disabled={
                                  selectableTicketNumbers.length === 0 ||
                                  ticketBulkBusy
                                }
                                onChange={toggleSelectAllTickets}
                              />
                            </th>
                          )}
                          <th>#</th>
                          <th>Tipo</th>
                          <th>Assunto</th>
                          <th>Autor</th>
                          <th>Status</th>
                          <th>Fechado por</th>
                          <th>Aberto em</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tickets.length === 0 ? (
                          <tr>
                            <td
                              colSpan={ticketBulkEnabled ? 8 : 7}
                              className="admin-empty"
                            >
                              Nenhum ticket encontrado.
                            </td>
                          </tr>
                        ) : (
                          tickets.map((t) => {
                            const selectable =
                              ticketBulkEnabled &&
                              (t.status === 'closed' || t.status === 'deleted');
                            const isSelected = ticketSelected.some(
                              (s) => String(s) === String(t.ticketNumber)
                            );
                            return (
                              <tr
                                key={`${t.ticketNumber}-${t.status}`}
                                onClick={() => openTicketDetail(t.ticketNumber)}
                                className={`admin-table__row${
                                  isSelected ? ' is-selected' : ''
                                }${selectable ? ' admin-table__row--selectable' : ''}`}
                              >
                                {ticketBulkEnabled && (
                                  <td
                                    className="admin-table__check"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <input
                                      type="checkbox"
                                      className="admin-check"
                                      aria-label={`Selecionar ticket #${t.ticketNumber}`}
                                      checked={isSelected}
                                      disabled={!selectable || ticketBulkBusy}
                                      onClick={(e) => e.stopPropagation()}
                                      onChange={(e) => {
                                        if (!selectable || ticketBulkBusy) return;
                                        toggleTicketSelected(t.ticketNumber, {
                                          shiftKey: Boolean(e.nativeEvent?.shiftKey),
                                        });
                                      }}
                                    />
                                  </td>
                                )}
                                <td>#{t.ticketNumber}</td>
                                <td>
                                  {t.typeEmoji} {t.typeLabel}
                                </td>
                                <td>{t.assunto || '—'}</td>
                                <td>
                                  <DiscordUserChip
                                    user={t.user}
                                    userId={t.userId}
                                    fallbackTag={t.userTag}
                                    compact
                                  />
                                </td>
                                <td>
                                  <span className={`admin-badge admin-badge--${t.status}`}>
                                    {STATUS_LABEL[t.status] || t.status}
                                  </span>
                                </td>
                                <td>
                                  <DiscordUserChip
                                    user={t.closedByUser}
                                    userId={t.closedBy}
                                    compact
                                  />
                                </td>
                                <td>{formatDate(t.createdAt)}</td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <>
                  <header className="admin-section__head admin-section__head--row">
                    <button
                      type="button"
                      className="admin-btn admin-btn--ghost admin-btn--sm"
                      onClick={closeDetail}
                    >
                      <FaArrowLeft aria-hidden="true" />
                      Voltar
                    </button>
                    <div>
                      <h1>Ticket #{selectedNumber}</h1>
                      <p>Detalhes e log de eventos</p>
                    </div>
                  </header>

                  {ticketDetail && (
                    <div className="admin-detail">
                      <div className="admin-panel">
                        <dl className="admin-dl">
                          <div>
                            <dt>Status</dt>
                            <dd>
                              <span
                                className={`admin-badge admin-badge--${ticketDetail.status}`}
                              >
                                {STATUS_LABEL[ticketDetail.status]}
                              </span>
                            </dd>
                          </div>
                          <div>
                            <dt>Tipo</dt>
                            <dd>
                              {ticketDetail.typeEmoji} {ticketDetail.typeLabel}
                            </dd>
                          </div>
                          <div>
                            <dt>Autor</dt>
                            <dd>
                              {ticketDetail.userTag}{' '}
                              <span className="admin-mono">({ticketDetail.userId})</span>
                            </dd>
                          </div>
                          <div>
                            <dt>Assunto</dt>
                            <dd>{ticketDetail.assunto || '—'}</dd>
                          </div>
                          <div>
                            <dt>Motivo</dt>
                            <dd className="admin-pre">{ticketDetail.motivo || '—'}</dd>
                          </div>
                          <div>
                            <dt>Assumido por</dt>
                            <dd>
                              <DiscordUserChip
                                user={ticketDetail.claimedByUser}
                                userId={ticketDetail.claimedBy}
                              />
                            </dd>
                          </div>
                          <div>
                            <dt>Fechado por</dt>
                            <dd>
                              <DiscordUserChip
                                user={ticketDetail.closedByUser}
                                userId={ticketDetail.closedBy}
                              />
                            </dd>
                          </div>
                          <div>
                            <dt>Avaliação</dt>
                            <dd>
                              {ticketDetail.rating != null
                                ? `${ticketDetail.rating}/5`
                                : '—'}
                              {ticketDetail.ratingComment
                                ? ` — ${ticketDetail.ratingComment}`
                                : ''}
                            </dd>
                          </div>
                        </dl>

                        <div className="admin-detail__actions">
                          {canTicketsEdit && ticketDetail.status === 'open' && (
                            <>
                              <button
                                type="button"
                                className="admin-btn admin-btn--primary admin-btn--sm"
                                disabled={
                                  Boolean(ticketActionBusy) ||
                                  (Boolean(ticketDetail.claimedBy) &&
                                    ticketDetail.claimedBy !== user?.id)
                                }
                                onClick={() => runTicketAction('claim')}
                              >
                                <FaUserCheck aria-hidden="true" />
                                {ticketDetail.claimedBy === user?.id
                                  ? 'Liberar'
                                  : ticketDetail.claimedBy
                                    ? 'Já assumido'
                                    : 'Assumir'}
                              </button>
                              <button
                                type="button"
                                className="admin-btn admin-btn--danger admin-btn--sm"
                                disabled={Boolean(ticketActionBusy)}
                                onClick={() =>
                                  setTicketConfirm({
                                    action: 'close',
                                    title: 'Fechar ticket',
                                    message: `Fechar o ticket #${selectedNumber}? O autor perde o acesso ao canal.`,
                                    confirmLabel: 'Fechar',
                                    tone: 'danger',
                                  })
                                }
                              >
                                <FaLock aria-hidden="true" />
                                Fechar
                              </button>
                            </>
                          )}
                          {canTicketsEdit && ticketDetail.status === 'closed' && (
                            <button
                              type="button"
                              className="admin-btn admin-btn--primary admin-btn--sm"
                              disabled={Boolean(ticketActionBusy)}
                              onClick={() =>
                                setTicketConfirm({
                                  action: 'reopen',
                                  title: 'Reabrir ticket',
                                  message: `Reabrir o ticket #${selectedNumber}? O autor volta a ter acesso.`,
                                  confirmLabel: 'Reabrir',
                                  tone: 'primary',
                                })
                              }
                            >
                              <FaUnlock aria-hidden="true" />
                              Reabrir
                            </button>
                          )}
                          {canTicketsEdit && ticketDetail.status !== 'deleted' && (
                            <button
                              type="button"
                              className="admin-btn admin-btn--ghost admin-btn--sm"
                              disabled={Boolean(ticketActionBusy)}
                              onClick={() =>
                                setTicketConfirm({
                                  action: 'transcript',
                                  title: 'Gerar transcrição',
                                  message:
                                    'Isso lê o histórico do canal e publica o link. Pode levar alguns segundos.',
                                  confirmLabel: 'Gerar',
                                  tone: 'primary',
                                })
                              }
                            >
                              <FaFileAlt aria-hidden="true" />
                              {ticketActionBusy === 'transcript'
                                ? 'Gerando…'
                                : ticketDetail.transcriptUrl
                                  ? 'Regenerar transcrição'
                                  : 'Gerar transcrição'}
                            </button>
                          )}
                          {ticketDetail.transcriptUrl && (
                            <a
                              href={ticketDetail.transcriptUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="admin-btn admin-btn--primary admin-btn--sm"
                            >
                              Ver histórico
                              <FaExternalLinkAlt aria-hidden="true" />
                            </a>
                          )}
                          {channelUrl && (
                            <a
                              href={channelUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="admin-btn admin-btn--ghost admin-btn--sm"
                            >
                              Abrir canal no Discord
                              <FaExternalLinkAlt aria-hidden="true" />
                            </a>
                          )}
                        </div>

                        {canTicketsEdit && ticketDetail.status === 'open' && (
                          <div className="admin-ticket-reply">
                            <label htmlFor="admin-ticket-reply">
                              <FaCommentDots aria-hidden="true" />
                              Responder no canal
                            </label>
                            <textarea
                              id="admin-ticket-reply"
                              className="admin-textarea"
                              rows={4}
                              maxLength={1900}
                              placeholder="Mensagem que será enviada no canal do ticket…"
                              value={ticketReply}
                              disabled={Boolean(ticketActionBusy)}
                              onChange={(e) => setTicketReply(e.target.value)}
                            />
                            <div className="admin-ticket-reply__bar">
                              <span className="admin-hint admin-hint--inline">
                                {ticketReply.length}/1900
                              </span>
                              <button
                                type="button"
                                className="admin-btn admin-btn--primary admin-btn--sm"
                                disabled={
                                  Boolean(ticketActionBusy) ||
                                  !ticketReply.trim()
                                }
                                onClick={() =>
                                  runTicketAction('reply', {
                                    content: ticketReply.trim(),
                                  })
                                }
                              >
                                {ticketActionBusy === 'reply'
                                  ? 'Enviando…'
                                  : 'Enviar'}
                              </button>
                            </div>
                          </div>
                        )}

                        {!canTicketsEdit && (
                          <p className="admin-hint admin-hint--inline">
                            Seu acesso a Tickets é somente leitura. Peça ao dono
                            a permissão <strong>Gerenciar</strong> para assumir,
                            fechar ou responder.
                          </p>
                        )}

                        {ticketActionMsg && (
                          <p className="admin-flash admin-flash--ok">
                            {ticketActionMsg}
                          </p>
                        )}
                        {ticketActionError && (
                          <p className="admin-flash admin-flash--err">
                            {ticketActionError}
                          </p>
                        )}
                        {canTicketsEdit &&
                          !ticketDetail.transcriptUrl &&
                          ticketDetail.status !== 'deleted' && (
                          <p className="admin-hint admin-hint--inline">
                            Sem transcrição ainda. Use <strong>Gerar transcrição</strong> ou
                            o botão no Discord.
                          </p>
                        )}
                      </div>

                      <div className="admin-panel">
                        <h2>Log do ticket</h2>
                        <p className="admin-hint">
                          Eventos do atendimento. O histórico de mensagens fica na
                          transcrição (quando gerada).
                        </p>
                        <ol className="admin-timeline">
                          {timeline.map((ev, i) => (
                            <li key={`${ev.type}-${i}`}>
                              <span className="admin-timeline__dot" aria-hidden="true" />
                              <div>
                                <strong>{ev.label}</strong>
                                <time dateTime={ev.at || undefined}>
                                  {formatDate(ev.at)}
                                </time>
                                {(ev.byUser || ev.byTag || ev.by) && (
                                  <p className="admin-timeline__by">
                                    Por{' '}
                                    <DiscordUserChip
                                      user={ev.byUser}
                                      userId={ev.by}
                                      fallbackTag={ev.byTag}
                                      compact
                                    />
                                  </p>
                                )}
                                {ev.comment && <p>{ev.comment}</p>}
                              </div>
                            </li>
                          ))}
                        </ol>
                      </div>
                    </div>
                  )}
                </>
              )}
            </section>
          )}

          {tab === 'ratings' && (
            <section className="admin-section">
              <header className="admin-section__head">
                <h1>Avaliações de ticket</h1>
                <p>Média, distribuição e comentários dos atendimentos</p>
              </header>

              <div className="admin-stats admin-stats--3">
                <div className="admin-stat">
                  <span className="admin-stat__label">Média</span>
                  <strong className="admin-stat__value">
                    {ratingStats?.average != null ? ratingStats.average.toFixed(1) : '—'}
                  </strong>
                </div>
                <div className="admin-stat">
                  <span className="admin-stat__label">Total</span>
                  <strong className="admin-stat__value">{ratingStats?.total ?? '—'}</strong>
                </div>
                <div className="admin-stat">
                  <span className="admin-stat__label">Com comentário</span>
                  <strong className="admin-stat__value">
                    {ratingStats?.withComments ?? '—'}
                  </strong>
                </div>
              </div>

              <div className="admin-panel">
                <h2>Distribuição</h2>
                <div className="admin-rating-bars">
                  {[5, 4, 3, 2, 1].map((stars) => {
                    const count = ratingStats?.distribution?.[stars] || 0;
                    const total = ratingStats?.total || 0;
                    const pct = total ? Math.round((count / total) * 100) : 0;
                    return (
                      <div key={stars} className="admin-rating-bar">
                        <span className="admin-rating-bar__label">
                          {stars}★
                        </span>
                        <div className="admin-rating-bar__track">
                          <div
                            className="admin-rating-bar__fill"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <span className="admin-rating-bar__count">
                          {count} ({pct}%)
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {canRatingsDelete && (
              <div className="admin-bulkbar">
                <label className="admin-bulkbar__select-all">
                  <input
                    type="checkbox"
                    className="admin-check"
                    checked={allRatingsSelected}
                    disabled={ratings.length === 0 || ratingBulkBusy}
                    onChange={toggleSelectAllRatings}
                  />
                  <span>
                    {ratingSelected.length > 0
                      ? `${ratingSelected.length} selecionada${
                          ratingSelected.length === 1 ? '' : 's'
                        }`
                      : 'Selecionar tudo'}
                  </span>
                </label>
                <p className="admin-bulkbar__hint">
                  Avaliações ficam arquivadas mesmo após apagar o ticket. Use
                  excluir ou limpe as órfãs (sem ticket no banco).
                </p>
                <div className="admin-bulkbar__actions">
                  <button
                    type="button"
                    className="admin-btn admin-btn--ghost admin-btn--sm"
                    disabled={ratingBulkBusy || !ratings.length}
                    onClick={() =>
                      setTicketConfirm({
                        kind: 'ratings-orphans',
                        title: 'Limpar avaliações órfãs?',
                        message:
                          'Remove do banco todas as avaliações cujo ticket já não existe mais. Não dá para desfazer.',
                        confirmLabel: 'Limpar órfãs',
                        tone: 'danger',
                      })
                    }
                  >
                    Limpar órfãs
                  </button>
                  <button
                    type="button"
                    className="admin-btn admin-btn--danger admin-btn--sm"
                    disabled={!ratingSelected.length || ratingBulkBusy}
                    onClick={() =>
                      setTicketConfirm({
                        kind: 'ratings-delete',
                        title: 'Excluir avaliações permanentemente?',
                        message: `Isso remove ${ratingSelected.length} avaliação(ões) do banco de dados. Não dá para desfazer.`,
                        confirmLabel: 'Excluir do banco',
                        tone: 'danger',
                      })
                    }
                  >
                    <FaTrash aria-hidden="true" />
                    {ratingBulkBusy ? 'Excluindo…' : 'Excluir selecionadas'}
                  </button>
                </div>
              </div>
              )}

              {!canRatingsDelete && (
                <p className="admin-hint">
                  Seu acesso a Avaliações é somente leitura.
                </p>
              )}

              {(ratingBulkMsg || ratingBulkError) && (
                <div className="admin-bulkbar__feedback">
                  {ratingBulkMsg && (
                    <p className="admin-flash admin-flash--ok">{ratingBulkMsg}</p>
                  )}
                  {ratingBulkError && (
                    <p className="admin-flash admin-flash--err">{ratingBulkError}</p>
                  )}
                </div>
              )}

              <div className="admin-table-wrap">
                <table className="admin-table">
                  <thead>
                    <tr>
                      {canRatingsDelete && (
                        <th className="admin-table__check">
                          <input
                            type="checkbox"
                            className="admin-check"
                            aria-label="Selecionar todas as avaliações"
                            checked={allRatingsSelected}
                            disabled={ratings.length === 0 || ratingBulkBusy}
                            onChange={toggleSelectAllRatings}
                          />
                        </th>
                      )}
                      <th>#</th>
                      <th>Nota</th>
                      <th>Autor</th>
                      <th>Tipo</th>
                      <th>Comentário</th>
                      <th>Data</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ratings.length === 0 ? (
                      <tr>
                        <td colSpan={canRatingsDelete ? 7 : 6} className="admin-empty">
                          Nenhuma avaliação registrada ainda.
                        </td>
                      </tr>
                    ) : (
                      ratings.map((r) => {
                        const isSelected = ratingSelected.some(
                          (s) => String(s) === String(r.ticketNumber)
                        );
                        return (
                          <tr
                            key={`${r.ticketNumber}-${r.ratedAt || r.stars}`}
                            className={`admin-table__row${
                              isSelected ? ' is-selected' : ''
                            }`}
                            onClick={() => {
                              setTab('tickets');
                              openTicketDetail(r.ticketNumber);
                            }}
                          >
                            {canRatingsDelete && (
                              <td
                                className="admin-table__check"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <input
                                  type="checkbox"
                                  className="admin-check"
                                  aria-label={`Selecionar avaliação #${r.ticketNumber}`}
                                  checked={isSelected}
                                  disabled={ratingBulkBusy}
                                  onClick={(e) => e.stopPropagation()}
                                  onChange={(e) => {
                                    if (ratingBulkBusy) return;
                                    toggleRatingSelected(r.ticketNumber, {
                                      shiftKey: Boolean(e.nativeEvent?.shiftKey),
                                    });
                                  }}
                                />
                              </td>
                            )}
                            <td>#{r.ticketNumber}</td>
                            <td>
                              <span className="admin-stars">
                                {'★'.repeat(r.stars)}
                                <span className="admin-stars__empty">
                                  {'★'.repeat(Math.max(0, 5 - r.stars))}
                                </span>
                              </span>
                            </td>
                            <td>
                              <DiscordUserChip
                                user={r.user}
                                userId={r.userId}
                                fallbackTag={r.userTag}
                                compact
                              />
                            </td>
                            <td>
                              {r.typeEmoji} {r.typeLabel || '—'}
                            </td>
                            <td>{r.comment || '—'}</td>
                            <td>{formatDate(r.ratedAt)}</td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {tab === 'support_chat' && (
            <SupportDeskPanel
              canEdit={canSupportChatEdit}
              canDelete={canSupportChatDelete}
              canChat={canSupportChatChat}
              canClaim={canSupportChatClaim}
              canRelease={canSupportChatRelease}
              canClose={canSupportChatClose}
              canTransfer={canSupportChatTransfer}
              canReport={canSupportChatReport}
              canVipEdit={canSupportChatVipGrant}
              guildMembers={guildMembers}
              guildMembersLoading={guildMembersLoading}
              onReloadMembers={() => loadGuildMembers().catch(handleAuthError)}
              guildChannels={guildChannels}
              guildChannelsLoading={guildResourcesLoading}
              guildRoles={guildRoles}
              onReloadChannels={() =>
                loadGuildResources().catch(handleAuthError)
              }
              focusChatId={supportFocusChatId}
              onFocusChatConsumed={() => {
                setSupportFocusChatId(null);
                setSearchParams(
                  (prev) => {
                    const next = new URLSearchParams(prev);
                    next.delete('chat');
                    if (next.get('tab') === 'support_chat') {
                      /* mantém tab */
                    }
                    return next;
                  },
                  { replace: true }
                );
              }}
              onOpenVip={(discordId) => {
                const q = String(discordId || '');
                setVipQuery(q);
                vipQueryRef.current = q;
                setVipStatus('all');
                setTab('vip');
              }}
              onOpenProfiles={(discordId) => {
                const q = String(discordId || '');
                setProfilesQuery(q);
                profilesQueryRef.current = q;
                setTab('profiles');
              }}
              onAuthError={handleAuthError}
            />
          )}

          {tab === 'vip' && (
            <VipPanel
              stats={vipStats}
              subscriptions={vipList}
              total={vipTotal}
              catalog={vipCatalog}
              statusFilter={vipStatus}
              planFilter={vipPlanFilter}
              query={vipQuery}
              loading={loading}
              actionBusy={vipActionBusy}
              message={vipMsg}
              error={vipError}
              canEdit={canVipEdit}
              canDelete={canVipDelete}
              onStatusFilter={(s) => setVipStatus(s)}
              onPlanFilter={(p) => setVipPlanFilter(p)}
              onQueryChange={(v) => {
                setVipQuery(v);
                vipQueryRef.current = v;
              }}
              onSearch={() => loadVip().catch(handleAuthError)}
              onRefresh={() => loadVip().catch(handleAuthError)}
              onSelect={setVipSelected}
              selected={vipSelected}
              onCloseDetail={() => setVipSelected(null)}
              onRevoke={(id) => runVipAction('revoke', { id })}
              onReapply={(id) => runVipAction('reapply', { id })}
              onReactivate={(id, patch = {}) =>
                runVipAction('reactivate', { id, ...patch })
              }
              onDelete={(id) => runVipAction('delete', { id })}
              onUpdate={(id, patch) => runVipAction('update', { id, ...patch })}
              onGrant={(form) => runVipAction('grant', form)}
              grantOpen={vipGrantOpen}
              onToggleGrant={() => setVipGrantOpen((v) => !v)}
              guildMembers={guildMembers}
              guildMembersLoading={guildMembersLoading}
              guildMembersError={guildMembersError}
              onReloadMembers={() => loadGuildMembers().catch(handleAuthError)}
            />
          )}

          {tab === 'marketing' && (
            <Suspense fallback={<p style={{ color: 'var(--stone)' }}>Carregando Marketing &amp; Finanças…</p>}>
              <MarketingFinancePanel
                canCosts={canMarketingCosts}
                canCoupons={canMarketingCoupons}
                canPromos={canMarketingPromos}
                canCreators={canMarketingCreators}
                canPricing={canMarketingPricing}
                canExport={canMarketingExport}
                canDelete={canMarketingDelete}
                onAuthError={handleAuthError}
                guildMembers={guildMembers}
              />
            </Suspense>
          )}

          {tab === 'profiles' && (
            <LinkedProfilesPanel
              stats={profilesStats}
              profiles={profilesList}
              pendingCodes={profilesPendingCodes}
              total={profilesTotal}
              presenceFilter={profilesPresence}
              query={profilesQuery}
              loading={loading}
              actionBusy={profilesActionBusy}
              message={profilesMsg}
              error={profilesError}
              canEdit={canProfilesEdit}
              canDelete={canProfilesDelete}
              onPresenceFilter={(s) => setProfilesPresence(s)}
              onQueryChange={(v) => {
                setProfilesQuery(v);
                profilesQueryRef.current = v;
              }}
              onSearch={() => loadProfiles().catch(handleAuthError)}
              onRefresh={() => loadProfiles().catch(handleAuthError)}
              onSelect={setProfilesSelected}
              selected={profilesSelected}
              onCloseDetail={() => setProfilesSelected(null)}
              onUnlink={(id) => runProfilesAction('unlink', { id })}
              onUpdate={(id, patch) =>
                runProfilesAction('update', { id, ...patch })
              }
              onRevokeCode={(code) =>
                runProfilesAction('revoke_code', {
                  codeId: code.id,
                  code: code.code,
                })
              }
              linkOpen={profilesLinkOpen}
              onToggleLink={() => setProfilesLinkOpen((v) => !v)}
              onLink={(form) => runProfilesAction('link', form)}
              guildMembers={guildMembers}
              guildMembersLoading={guildMembersLoading}
              guildMembersError={guildMembersError}
              onReloadMembers={() => loadGuildMembers().catch(handleAuthError)}
            />
          )}

          {tab === 'team' && (
            <TeamPanel
              stats={teamStats}
              members={teamList}
              total={teamTotal}
              catalog={teamCatalog}
              visibilityFilter={teamVisibility}
              query={teamQuery}
              loading={loading}
              actionBusy={teamActionBusy}
              message={teamMsg}
              error={teamError}
              canEdit={canTeamEdit}
              canDelete={canTeamDelete}
              onVisibilityFilter={(s) => setTeamVisibility(s)}
              onQueryChange={(v) => {
                setTeamQuery(v);
                teamQueryRef.current = v;
              }}
              onSearch={() => loadTeam().catch(handleAuthError)}
              onRefresh={() => loadTeam().catch(handleAuthError)}
              onSelect={setTeamSelected}
              selected={teamSelected}
              onCloseDetail={() => setTeamSelected(null)}
              createOpen={teamCreateOpen}
              onToggleCreate={() => setTeamCreateOpen((v) => !v)}
              onCreate={(form) => runTeamAction('create', form)}
              onUpdate={(id, patch) => runTeamAction('update', { id, ...patch })}
              onDelete={(id) => runTeamAction('delete', { id })}
              onReorder={(ids) => runTeamAction('reorder', { ids })}
              onCreateBadge={async (payload) => {
                const data = await runTeamAction('create_badge', payload);
                return data?.badge || null;
              }}
              onDeleteBadge={(id) => runTeamAction('delete_badge', { id })}
              guildMembers={guildMembers}
              guildMembersLoading={guildMembersLoading}
              guildMembersError={guildMembersError}
              onReloadMembers={() => loadGuildMembers().catch(handleAuthError)}
            />
          )}

          {tab === 'performance' && (
            <PerformancePanel
              latest={perfLatest}
              history={perfHistory}
              heartbeat={perfHeartbeat}
              servers={perfServers}
              loading={loading}
              error={perfError}
              onRefresh={() =>
                loadPerformance()
                  .catch((err) => {
                    setPerfError(err.message || 'Falha ao carregar performance');
                    handleAuthError(err);
                  })
              }
            />
          )}

          {tab === 'changelog' && (
            <ChangelogPanel
              entries={changelogEntries}
              total={changelogTotal}
              catalog={changelogCatalog}
              areaFilter={changelogArea}
              query={changelogQuery}
              loading={loading}
              onAreaFilter={(a) => setChangelogArea(a)}
              onQueryChange={(v) => {
                setChangelogQuery(v);
                changelogQueryRef.current = v;
              }}
              onSearch={() => loadChangelog().catch(handleAuthError)}
              onRefresh={() => loadChangelog().catch(handleAuthError)}
            />
          )}

          {tab === 'welcome' && welcomeSettings && (
            <WelcomeSettingsPanel
              settings={welcomeSettings}
              templates={welcomeTemplates}
              placeholders={welcomePlaceholders}
              maxButtons={welcomeMaxButtons}
              guildChannels={guildChannels}
              guildResourcesLoading={guildResourcesLoading}
              saving={welcomeSaving}
              message={welcomeMsg}
              canEdit={canWelcomeEdit}
              onChange={setWelcomeSettings}
              onSave={saveWelcomeSettings}
              onApplyTemplate={applyWelcomeTemplate}
            />
          )}

          {tab === 'messages' && (
            <MessageComposerPanel
              guildChannels={guildChannels}
              guildResourcesLoading={guildResourcesLoading}
              sending={messageSending}
              message={messageMsg}
              error={messageError}
              lastResult={messageLastResult}
              canEdit={canMessagesEdit}
              onSend={handleSendMessage}
            />
          )}

          {tab === 'welcome' && !welcomeSettings && loading && (
            <p className="admin-empty">Carregando boas-vindas…</p>
          )}

          {tab === 'permissions' && isOwner && panelPermissions && (
            <PermissionsPanel
              settings={panelPermissions}
              catalog={permissionsCatalog}
              guildRoles={guildRoles}
              guildMembers={guildMembers}
              guildMembersLoading={guildMembersLoading}
              guildMembersError={guildMembersError}
              guildResourcesLoading={guildResourcesLoading}
              saving={permissionsSaving}
              message={permissionsMsg}
              onChange={setPanelPermissions}
              onSave={savePermissions}
            />
          )}

          {tab === 'permissions' && isOwner && !panelPermissions && loading && (
            <p className="admin-empty">Carregando permissões…</p>
          )}

          {tab === 'audit' && auditSettings && (
            <section className="admin-section">
              <header className="admin-section__head">
                <h1>Logs de Auditoria</h1>
                <p>
                  Canal, eventos e filtros do modlog profissional do Discord
                  ({auditEnabledCount} eventos ativos)
                </p>
              </header>

              {auditMsg && <p className="admin-success">{auditMsg}</p>}

              <div className="admin-form">
                <fieldset className="admin-fieldset">
                  <legend>Geral</legend>
                  <label className="admin-switch">
                    <span>
                      <strong>Sistema ativo</strong>
                      <small>
                        Quando desligado, nenhum log é enviado — mesmo com canal
                        e eventos marcados.
                      </small>
                    </span>
                    <input
                      type="checkbox"
                      checked={Boolean(auditSettings.enabled)}
                      onChange={(e) => updateAuditField('enabled', e.target.checked)}
                    />
                  </label>
                  <label>
                    Canal de logs
                    <select
                      value={auditSettings.channelId || ''}
                      onChange={(e) =>
                        updateAuditField('channelId', e.target.value || null)
                      }
                      disabled={guildResourcesLoading}
                    >
                      <option value="">
                        {guildResourcesLoading
                          ? 'Carregando canais…'
                          : 'Selecione um canal de texto'}
                      </option>
                      {guildChannels.map((ch) => (
                        <option key={ch.id} value={ch.id}>
                          {ch.parentName
                            ? `#${ch.name} (${ch.parentName})`
                            : `#${ch.name}`}
                        </option>
                      ))}
                      {auditSettings.channelId &&
                        !guildChannels.some(
                          (ch) => ch.id === String(auditSettings.channelId)
                        ) && (
                          <option value={auditSettings.channelId}>
                            Canal salvo ({auditSettings.channelId})
                          </option>
                        )}
                    </select>
                    {!guildResourcesLoading && guildChannels.length === 0 && (
                      <small className="admin-field-hint">
                        Nenhum canal de texto encontrado. Confira se o bot está no
                        servidor.
                      </small>
                    )}
                  </label>
                </fieldset>

                <fieldset className="admin-fieldset">
                  <legend>Comportamento</legend>
                  <label className="admin-switch">
                    <span>
                      <strong>Ignorar bots</strong>
                      <small>Não registrar ações/mensagens de contas bot.</small>
                    </span>
                    <input
                      type="checkbox"
                      checked={Boolean(auditSettings.ignoreBots)}
                      onChange={(e) => updateAuditField('ignoreBots', e.target.checked)}
                    />
                  </label>
                  <label className="admin-switch">
                    <span>
                      <strong>Ignorar o Nexus</strong>
                      <small>Não logar ações do próprio bot (evita spam).</small>
                    </span>
                    <input
                      type="checkbox"
                      checked={Boolean(auditSettings.ignoreSelf)}
                      onChange={(e) => updateAuditField('ignoreSelf', e.target.checked)}
                    />
                  </label>
                  <label className="admin-switch">
                    <span>
                      <strong>Mostrar responsável</strong>
                      <small>
                        Consulta o Audit Log do Discord (quem baniu, deletou,
                        kickou…). O bot precisa da permissão “Ver registro de
                        auditoria”.
                      </small>
                    </span>
                    <input
                      type="checkbox"
                      checked={Boolean(auditSettings.showExecutor)}
                      onChange={(e) => updateAuditField('showExecutor', e.target.checked)}
                    />
                  </label>
                </fieldset>

                <fieldset className="admin-fieldset">
                  <legend>Filtros (opcional)</legend>
                  <AdminMultiSelect
                    label="Ignorar canais"
                    hint="Eventos nestes canais não geram log"
                    options={guildChannels}
                    value={auditIgnoreChannels}
                    onChange={setAuditIgnoreChannelsList}
                    loading={guildResourcesLoading}
                    placeholder="Buscar canal…"
                    emptyLabel="Nenhum canal disponível"
                    getOptionLabel={(ch) => `#${ch.name}`}
                    getOptionMeta={(ch) => ch.parentName || ''}
                  />
                  <AdminMultiSelect
                    label="Ignorar cargos"
                    hint="Membros com estes cargos não geram log de entrada/saída/cargos"
                    options={guildRoles}
                    value={auditIgnoreRoles}
                    onChange={setAuditIgnoreRolesList}
                    loading={guildResourcesLoading}
                    placeholder="Buscar cargo…"
                    emptyLabel="Nenhum cargo disponível"
                    getOptionLabel={(role) => role.name}
                    getOptionColor={(role) => roleColorHex(role.color)}
                  />
                </fieldset>

                <fieldset className="admin-fieldset">
                  <legend>Eventos monitorados</legend>
                  <div className="admin-audit__toolbar">
                    <button
                      type="button"
                      className="admin-btn admin-btn--ghost admin-btn--sm"
                      onClick={() => setAllAuditEvents(true)}
                    >
                      Ativar todos
                    </button>
                    <button
                      type="button"
                      className="admin-btn admin-btn--ghost admin-btn--sm"
                      onClick={() => setAllAuditEvents(false)}
                    >
                      Desativar todos
                    </button>
                  </div>

                  <div className="admin-audit__groups">
                    {Object.entries(auditEventsByGroup).map(([group, events]) => {
                      if (!events.length) return null;
                      const groupLabel =
                        (auditCatalog.groups && auditCatalog.groups[group]) ||
                        FALLBACK_AUDIT_GROUPS[group] ||
                        group;
                      return (
                        <div key={group} className="admin-audit__group">
                          <div className="admin-audit__group-head">
                            <h3>{groupLabel}</h3>
                            <div className="admin-audit__group-actions">
                              <button
                                type="button"
                                className="admin-btn admin-btn--ghost admin-btn--sm"
                                onClick={() => setGroupAuditEvents(group, true)}
                              >
                                Todos
                              </button>
                              <button
                                type="button"
                                className="admin-btn admin-btn--ghost admin-btn--sm"
                                onClick={() => setGroupAuditEvents(group, false)}
                              >
                                Nenhum
                              </button>
                            </div>
                          </div>
                          {events.map((def) => (
                            <label key={def.id} className="admin-switch">
                              <span>{def.label}</span>
                              <input
                                type="checkbox"
                                checked={Boolean(auditSettings.events?.[def.id])}
                                onChange={(e) =>
                                  updateAuditEvent(def.id, e.target.checked)
                                }
                              />
                            </label>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                </fieldset>

                <div className="admin-form__actions">
                  <button
                    type="button"
                    className="admin-btn admin-btn--primary"
                    onClick={saveAuditSettings}
                    disabled={auditSaving || !canAuditEdit}
                  >
                    {auditSaving
                      ? 'Salvando…'
                      : canAuditEdit
                        ? 'Salvar auditoria'
                        : 'Somente leitura'}
                  </button>
                </div>
              </div>
            </section>
          )}

          {tab === 'audit' && !auditSettings && loading && (
            <p className="admin-empty">Carregando logs de auditoria…</p>
          )}

          {tab === 'site' && siteConfig && (
            <section className="admin-section admin-section--narrow">
              <header className="admin-section__head">
                <h1>Site e landing</h1>
                <p>Coming soon e toggles de seções/páginas</p>
              </header>

              {siteMsg && <p className="admin-success">{siteMsg}</p>}

              <div className="admin-form">
                <fieldset className="admin-fieldset">
                  <legend>Modo do site</legend>
                  <label className="admin-switch">
                    <span>
                      <strong>Coming soon</strong>
                      <small>
                        Mostra a página “Em breve” para visitantes. Admin e OAuth
                        continuam acessíveis.
                      </small>
                    </span>
                    <input
                      type="checkbox"
                      checked={Boolean(siteConfig.comingSoon)}
                      onChange={(e) =>
                        updateSiteToggle('root', 'comingSoon', e.target.checked)
                      }
                    />
                  </label>
                </fieldset>

                <fieldset className="admin-fieldset">
                  <legend>Seções da home</legend>
                  {Object.keys(SITE_SECTION_LABELS).map((key) => (
                    <label key={key} className="admin-switch">
                      <span>{SITE_SECTION_LABELS[key]}</span>
                      <input
                        type="checkbox"
                        checked={Boolean(siteConfig.sections?.[key])}
                        onChange={(e) =>
                          updateSiteToggle('sections', key, e.target.checked)
                        }
                      />
                    </label>
                  ))}
                </fieldset>

                <fieldset className="admin-fieldset">
                  <legend>Páginas</legend>
                  {Object.keys(SITE_PAGE_LABELS).map((key) => (
                    <label key={key} className="admin-switch">
                      <span>{SITE_PAGE_LABELS[key]}</span>
                      <input
                        type="checkbox"
                        checked={Boolean(siteConfig.pages?.[key])}
                        onChange={(e) =>
                          updateSiteToggle('pages', key, e.target.checked)
                        }
                      />
                    </label>
                  ))}
                </fieldset>

                <div className="admin-form__actions">
                  <button
                    type="button"
                    className="admin-btn admin-btn--primary"
                    onClick={saveSiteConfig}
                    disabled={siteSaving || !canSiteEdit}
                  >
                    {siteSaving
                      ? 'Salvando…'
                      : canSiteEdit
                        ? 'Salvar site'
                        : 'Somente leitura'}
                  </button>
                </div>
              </div>
            </section>
          )}

          {tab === 'site' && !siteConfig && loading && (
            <p className="admin-empty">Carregando configuração do site…</p>
          )}

          {tab === 'settings' && settings && (
            <section className="admin-section">
              <header className="admin-section__head">
                <h1>Configuração do bot</h1>
                <p>Altera as mesmas opções do comando /ticket</p>
              </header>

              {settingsMsg && <p className="admin-success">{settingsMsg}</p>}

              <div className="admin-form">
                <fieldset className="admin-fieldset">
                  <legend>Canais e cargos</legend>
                  <label>
                    Cargo da staff (ID)
                    <input
                      value={settings.staffRoleId || ''}
                      onChange={(e) => updateSettingField('staffRoleId', e.target.value)}
                      placeholder="ID do cargo"
                    />
                  </label>
                  <label>
                    Categoria padrão (ID)
                    <input
                      value={settings.categoryId || ''}
                      onChange={(e) => updateSettingField('categoryId', e.target.value)}
                    />
                  </label>
                  <label>
                    Canal de logs (ID)
                    <input
                      value={settings.logChannelId || ''}
                      onChange={(e) => updateSettingField('logChannelId', e.target.value)}
                    />
                  </label>
                </fieldset>

                <fieldset className="admin-fieldset">
                  <legend>Painel e tickets</legend>
                  <label>
                    Máx. tickets abertos por usuário
                    <input
                      type="number"
                      min={1}
                      max={10}
                      value={settings.maxOpenTickets ?? 1}
                      onChange={(e) =>
                        updateSettingField('maxOpenTickets', Number(e.target.value))
                      }
                    />
                  </label>
                  <label>
                    Padrão de nome
                    <input
                      value={settings.naming || ''}
                      onChange={(e) => updateSettingField('naming', e.target.value)}
                    />
                  </label>
                  <label>
                    Título do painel
                    <input
                      value={settings.panelTitle || ''}
                      onChange={(e) => updateSettingField('panelTitle', e.target.value)}
                    />
                  </label>
                  <label>
                    Descrição do painel
                    <textarea
                      rows={3}
                      value={settings.panelDescription || ''}
                      onChange={(e) =>
                        updateSettingField('panelDescription', e.target.value)
                      }
                    />
                  </label>
                  <label>
                    Mensagem de boas-vindas
                    <textarea
                      rows={3}
                      value={settings.welcomeMessage || ''}
                      onChange={(e) =>
                        updateSettingField('welcomeMessage', e.target.value)
                      }
                    />
                  </label>
                </fieldset>

                <fieldset className="admin-fieldset">
                  <legend>Tipos de ticket</legend>
                  <div className="admin-types">
                    {(settings.types || []).map((t, i) => (
                      <div key={t.id || i} className="admin-type">
                        <div className="admin-type__row">
                          <input
                            className="admin-type__emoji"
                            value={t.emoji || ''}
                            onChange={(e) => updateType(i, 'emoji', e.target.value)}
                            aria-label="Emoji"
                          />
                          <input
                            value={t.label || ''}
                            onChange={(e) => updateType(i, 'label', e.target.value)}
                            placeholder="Nome"
                          />
                          <input
                            value={t.id || ''}
                            onChange={(e) => updateType(i, 'id', e.target.value)}
                            placeholder="id"
                            className="admin-mono"
                          />
                          <button
                            type="button"
                            className="admin-btn admin-btn--danger admin-btn--sm"
                            onClick={() => removeType(i)}
                            aria-label="Remover tipo"
                          >
                            <FaTrash aria-hidden="true" />
                          </button>
                        </div>
                        <input
                          value={t.description || ''}
                          onChange={(e) => updateType(i, 'description', e.target.value)}
                          placeholder="Descrição"
                        />
                      </div>
                    ))}
                  </div>
                  <button
                    type="button"
                    className="admin-btn admin-btn--ghost admin-btn--sm"
                    onClick={addType}
                  >
                    <FaPlus aria-hidden="true" />
                    Adicionar tipo
                  </button>
                </fieldset>

                <div className="admin-form__actions">
                  <button
                    type="button"
                    className="admin-btn admin-btn--primary"
                    onClick={saveSettings}
                    disabled={settingsSaving || !canSettingsEdit}
                  >
                    {settingsSaving
                      ? 'Salvando…'
                      : canSettingsEdit
                        ? 'Salvar configuração'
                        : 'Somente leitura'}
                  </button>
                </div>
              </div>
            </section>
          )}

          {tab === 'settings' && !settings && loading && (
            <p className="admin-empty">Carregando configuração…</p>
          )}
        </main>
      </div>

      <ConfirmDialog
        open={Boolean(ticketConfirm)}
        title={ticketConfirm?.title || ''}
        message={ticketConfirm?.message || ''}
        confirmLabel={ticketConfirm?.confirmLabel || 'Confirmar'}
        tone={ticketConfirm?.tone || 'danger'}
        busy={Boolean(ticketActionBusy) || ticketBulkBusy || ratingBulkBusy}
        onCancel={() => {
          if (!ticketActionBusy && !ticketBulkBusy && !ratingBulkBusy) {
            setTicketConfirm(null);
          }
        }}
        onConfirm={() => {
          if (ticketConfirm?.kind === 'bulk-delete') {
            runTicketBulkDelete();
            return;
          }
          if (ticketConfirm?.kind === 'ratings-delete') {
            runRatingBulkDelete('numbers');
            return;
          }
          if (ticketConfirm?.kind === 'ratings-orphans') {
            runRatingBulkDelete('orphans');
            return;
          }
          if (ticketConfirm?.action) {
            runTicketAction(ticketConfirm.action);
          }
        }}
      />

    </div>
  );
};

export default Admin;
